const en = {
  translation: {
    formats: {
      date: 'MM/DD/YYYY',
    },
    self: {
      title: 'CASSIE',
      fullTitle: 'Coastal Analyst System from Space Imagery Engine',
      shortDesc:
        'Collaborative web platform with geospatial observation and forecasting solutions applied to monitoring, mitigation and adaptation of the coastal zone in the face of climate change.',
      abstract:
        'The general objective of this project is to define technical and operational methodology and develop a web platform that provides information services derived from satellite and artificial intelligence (e.g. coastline, topography, bathymetry, coastal compression, hydrodynamics) to create collections applied to mitigation and adaptation of the coastal zone to climate change.',
      poweredBy: 'Powered by: Google Earth Engine',
      imageryProvider: 'Imagery Providers: NASA (Landsat) and ESA (Sentinel)',
      changeLanguage: 'Language',
    },
    home: {
      about: {
        title: 'Learn more about CASSIE',
      },
      instructions: {
        title: 'CASSIE User Guide',
        text: 'To use CASSIE, you must have a google account registered on the google earth engine platform.',
        btnEngineSingUp: 'First access',
        btnManual: 'User Guide',
        linkManual: 'https://drive.google.com/file/d/1wZ-0QSdwRXifyGloouHbo2AjlzTVmUGW/view?usp=sharing',
        btnVideo: 'Instruction Video',
        linkVideo: 'https://www.youtube.com/@CASSIE-Core',
      },
      baysqueeze: {
        title: 'BaySqueeze',
        text: 'The project seeks to contribute with actions and results for the preservation of our planet, by assuming the task of fulfilling the United Nations Sustainable Development Goals, as well as attending the national needs of knowledge about climate change, in parallel with the goals of the sub-network "Coastal Zones", of the Brazilian Research Network on Global Climate Change (Rede Clima). ',
        btn: 'Know more about the project',
      },
      riscport: {
        title: 'RiskPorts',
        text: 'The main goal of this project is to conduct a climate risk analysis (environmental and economic) and to determine possible adaptation measures, based on the future projection of environmental threats (e.g. storm waves), to increase the resilience of the port sector and adjacent areas in the Babitonga Bay, in an interdisciplinary and multi-institutional network context.',
        btn: 'Know more about the project',
      },
      members: {
        title: 'Team',
        roles: {
          researcher: 'Researcher',
          coord: 'Coordinator',
          coord_geral: 'General Coordinator',
          atp: 'Grant holder ATP-B',
          bolsista: 'Scholarship holder - Development',
          validacao: 'Validation',
          desenvolvimento: 'Development',
          fumdes: 'Grant holder FUMDES',
          capes: 'CAPES DTI-B Fellow',
        },
        links: {
          teamDescription: 'The Team',
          currentTeam: 'Current team',
          teamHistory: 'Team history',
        },
      },
      papers: {
        title: 'Publications',
        resumo_text: 'Abstract in Proceedings',
        paper_text: 'Paper in Journal',
        curso_text: 'Workshop',
        more: 'Learn More',
      },
      sponsor: {
        realiza: 'Developed by',
        apoio: 'Financial Support',
        apoio_desc:
          'Current Process No. 406603/2022-7. Previous: Process No. 441818/2020-0, CNPq MCTIC/CNPq - N° 21/2017; Process No. 441545/2017-3.',
        inst: 'Institutional support',
        partners: 'Project Partners',
        previousSections: {
          institutionalCurrent: 'Current Institutional Partners',
          closedPartnerships: 'Closed Partnerships',
        },
      },
      footer: {
        social: 'Social',
        copyright: 'All rights reserved. System by',
        help: {
          title: 'Help & Support',
          faq: 'FAQ',
          discussion: 'Discussions',
          problems: 'Known Issues',
          termsOfUse: 'Terms of Use',
          termsOfUseText:
            'The CASSIE code is public, open, and free, made available under the Creative Commons CC-BY-SA license, with source reference according to the following format: CASSIE - accessed on [date] through the link: https://cassiengine.org/',
        },
        contact: {
          title: 'Contact',
          manage: 'Project Management',
          techquest: 'Technical Questions',
        },
        resources: {
          title: 'Resources',
          license: 'License',
          code: 'Source Code',
        },
      },
      warning: {
        text: 'CASSIE is still under development.',
        link: 'See more',
      },
      cookies: {
        text: 'Our website uses cookies to improve navigation.',
        link: 'Accept',
      },
      cards: {
        projectSite: {
          title: 'Project Website',
          description: 'Visit the main project website and learn more about our initiatives.',
          button: 'Access',
        },
        survey: {
          title: 'Satisfaction Survey',
          description: 'Take part in our satisfaction survey and help us improve.',
          button: 'Access',
        },
        tool: {
          title: 'CASSIE Tool',
          description: 'Access the CASSIE tool to explore our remote sensing features.',
          button: 'Access',
        },
      },
    },
    faq: {
      title: 'Frequently Asked Questions',
      0: {
        title: 'How does CASSIE works?',
        text: 'CASSIE uses Google Earth Engine (GEE), a platform of parallel processing in geospatial data cloud (<a href="https://www.sciencedirect.com/science/article/pii/S0034425717302900" target="_blank">Gorelick et al., 2017</a>). CASSIE communicates with the GEE through an API (Application Programming Interface), which allows it to use the datasets and processing potential of the GEE.',
      },
      1: {
        title: 'What satellite image datasets are available at CASSIE?',
        text: 'Currently, CASSIE enables analysis with data from <a href="https://developers.google.com/earth-engine/datasets/catalog/COPERNICUS_S2" target="_blank">Sentinel 2 (MSI, TOA) Level-1C</a> and the Landsat collections (<a href="https://developers.google.com/earth-engine/datasets/catalog/LANDSAT_LT05_C01_T1_SR" target="_blank">Landsat 5 TM SR Tier 1</a>, <a href="https://developers.google.com/earth-engine/datasets/catalog/LANDSAT_LE07_C01_T1_SR" target="_blank">Landsat 7 ETM+ SR Tier 1</a>, <a href="https://developers.google.com/earth-engine/datasets/catalog/LANDSAT_LC08_C01_T1_SR" target="_blank">Landsat 8 OLI SR Tier 1</a>). When Tier 1 images (higher quality) aren\'t available for Landsat 7 and 8, CASSIE uses images from the <a href="https://developers.google.com/earth-engine/datasets/catalog/LANDSAT_LE07_C01_T2_TOA" target="_blank">Landsat 7 TOA Tier 2</a> and <a href="https://developers.google.com/earth-engine/datasets/catalog/LANDSAT_LC08_C01_T2_TOA" target="_blank">Landsat 8 TOA Tier 2</a>.',
      },
      2: {
        title: 'How do I access CASSIE?',
        text: 'CASSIE is available as an open source tool, available at <a href="http://cassie-stable.herokuapp.com/">this link</a>. The user needs a <a href="https://myaccount.google.com">Google account</a> registered (and approved) on <a href="https://signup.earthengine.google.com">Google Earth Engine plataform</a>. CASSIE\'s code is available at <a href="https://github.com/lia-univali/cassie">LIA\'s Github - Univali.',
      },
      3: {
        title: 'When I sign in with my Google Account, the page loads up indefinitely',
        text: 'To access and use CASSIE, the user account must be registered on the Google Earth Engine (GEE) platform. The registration can be done through this <a href="https://earthengine.google.com/signup/" target="_blank">link</a>. Once the registration is confirmed, the user can access and sign in to CASSIE with your Google account.',
      },
      4: {
        title: "My account is registered in Google Earth Engine, but I still can't access the tool",
        text: 'Registration on the Google Earth Engine platform is terminated with a confirmation received via email. In some cases, the confirmation email is received instantly after the registration is made. However, confirmation is not always instantaneous and can take time that the CASSIE team has no control over. If the user has made the registration and has not yet received the confirmation email, and considers that the waiting time is too long, the user should contact the <a href="https://developers.google.com/earth-engine/help">Google Earth Engine team</a>. If the user cannot enter even after confirmation, it is recommended to <a href="https://support.google.com/accounts/answer/32050?co=GENIE.Platform%3DDesktop&hl=en" target="_blank">clear the browser cookies.',
      },
    },
    problems: {
      title: 'Known Issues',
      text: 'CASSIE is still under development, some processes may fail. These are some known issues.',
      0: {
        title: 'BigData Analysis ',
        text: "Sometimes CASSIE Can't perform large analyses, with large region or with many images.",
      },
    },
    general: {
      loading: 'Loading...',
      cancel: 'Cancel',
      reset: 'Reset',
      next: 'Next',
      back: 'Back',
      geeAccessError:
        'Your account still is not allowed to use GEE API. You should complete your register using this link: https://earthengine.google.com/signup/',
    },
    auth: {
      signin: 'Sign in with Google',
      signout: 'Sign out',
      loading: 'Authenticating...',
    },
    color: {
      colorRed: 'Red',
      colorGreen: 'Green',
    },
    tour: {
      done: 'Done',
      of: 'of',
      help: 'Help',
      acquisition: {
        1: {
          info: 'In this Step you must choose the Satellite',
          'Sentinel-2': {
            info: 'Sentinel has higher spatial resolution and less data acquisition time (from 2013-2025)',
          },
          Landsat: {
            info: 'Landsat has lower spatial resolution and longer data acquisition time (from 1984 to 2025)',
          },
        },
        2: {
          info: 'Select the interest area for analysis',
          dataTraining: 'Import training data file (.csv)',
        },
        3: {
          period: 'Select a date range to review',
          point: 'Each point on the line is an image',
          cloud: 'Select an acceptable percentage of clouds in the image',
        },
        4: {
          id: 'The ID contains important information such as the date and time the image was captured',
          clouds: 'In this column we have calculated percentage of Clouds in the image',
          image: 'This column shows the image in question',
          action: 'In this last column, you can choose whether or not to include this image in the analysis',
        },
      },
      bathymetry: {
        moduleName: 'Bathymetry',
        exportCSVBathymetry: 'Export CSV - Bathymetry',
        exportCSVMetrics: 'Export CSV - Metrics',
      },
      csqueeze: {
        2: {
          info: '',
          aoi: 'Select the area of interest for analysis',
          mangrove:
            'This button allows you to view potential intertidal zones around the world using the Murray et al. (2022) collection.',
          importKml: 'It is also possible to import data from a KML file to draw the area of interest on the map.',
        },
        3: {
          info: '',
          years: 'Set two different years to generate the prediction study.',
        },
        4: {
          info: '',
          years: 'For each year, images with the lowest possible number of clouds should be chosen.',
          timeselector: 'The pink dots represent the available images for the year with the selected cloud filter.',
          viewImageButton:
            'After selecting the pink dot with the available image, click here to view it. If nothing happens, it is possible that the image is 100% covered by clouds. This can happen because the level of clouds is defined by the satellite collection and not all regions are accurate.',
          cloudselector:
            'In this selection, it is possible to define the percentage of clouds in the images. When changing this parameter, the bar with pink dots will be updated, leaving only images that fit the value.',
          tableImages:
            'The selected images will be shown in this table. It is necessary to select at least 1 image for each year to continue.',
        },
        5: {
          info: '',
          selectType:
            'Select all types to activate the drawing mode. When choosing the ground cover, click on the map to start drawing a rectangle in the region that represents that type. To draw the rectangle made, hold down CTRL and click on it or activate the "Remove polygon" button and click on the polygon. It is necessary to draw at least 1 mangrove polygon and another distinct polygon to continue.',
          falseColorButton:
            'If the image is not very clear, it is possible to activate the false color mask that uses the infrared band instead of the red band on the map.',
          availableDataset:
            'Datasets are ready-made collections mapped by users or organizations with ready-made data. If the desired year has an available dataset, it will be notified in the corner of the screen.',
          importPolygons:
            'It is also possible to import rectangles with a CSV file generated by the EXPORT button below.',
          generateRaster:
            'Exporting and clearing all rectangles is one of the available options. If you have already drawn at least 1 mangrove and another distinct rectangle, the "GENERATE RASTER" button will appear.',
          yearsButtons: "Don't forget to generate the rasters for both years to continue.",
        },
      },
      euscoast: {
        2: {
          info: '',
          aoi: 'Select the area of interest for analysis',
          importKml: 'It is also possible to import data from a KML file to draw the area of interest on the map.',
        },
        3: {
          info: '',
          years: 'Set two different years to generate the prediction study.',
        },
        4: {
          info: '',
          years: 'For each year, images with the lowest possible number of clouds should be chosen.',
          timeselector: 'The pink dots represent the available images for the year with the selected cloud filter.',
          viewImageButton:
            'After selecting the pink dot with the available image, click here to view it. If nothing happens, it is possible that the image is 100% covered by clouds. This can happen because the level of clouds is defined by the satellite collection and not all regions are accurate.',
          cloudselector:
            'In this selection, it is possible to define the percentage of clouds in the images. When changing this parameter, the bar with pink dots will be updated, leaving only images that fit the value.',
          tableImages:
            'The selected images will be shown in this table. It is necessary to select at least 1 image for each year to continue.',
        },
        5: {
          info: '',
          selectType:
            'Select all types to activate the drawing mode. When choosing the ground cover, click on the map to start drawing a rectangle in the region that represents that type. To draw the rectangle made, hold down CTRL and click on it or activate the "Remove polygon" button and click on the polygon. It is necessary to draw at least 1 mangrove polygon and another distinct polygon to continue.',
          falseColorButton:
            'If the image is not very clear, it is possible to activate the false color mask that uses the infrared band instead of the red band on the map.',
          availableDataset:
            'Datasets are ready-made collections mapped by users or organizations with ready-made data. If the desired year has an available dataset, it will be notified in the corner of the screen.',
          importPolygons:
            'It is also possible to import rectangles with a CSV file generated by the EXPORT button below.',
          generateRaster:
            'Exporting and clearing all rectangles is one of the available options. If you have already drawn at least 1 mangrove and another distinct rectangle, the "GENERATE RASTER" button will appear.',
          yearsButtons: "Don't forget to generate the rasters for both years to continue.",
        },
      },
      interdial: {
        2: {
          info: '',
          aoi: 'Select the area of interest for analysis',
          interdialZone: 'This button allows you to visualize possible interdial zones around the world',
          importKml: 'It is also possible to import data from a KML file to draw the area of interest on the map.',
        },
      },
      map: {
        start: 'This is the processing screen',
        imageChooser: {
          info: 'In this field you can load on the map one of the selected images',
          select: 'By default, the selected image is the most recent, but you can choose another one by clicking here',
          click: 'After selecting the Image, you can load it on the screen by pressing this button',
        },
      },
    },
    forms: {
      core: {
        pdfReport: {
          failed: 'The PDF report could not be generated.',
          tooltip: 'Download a PDF report with the whole analysis: area, imagery, results and map in a single document',
          subtitle: 'CASSIE · generated {{date}}',
          missingImage: 'The image "{{caption}}" could not be included: the browser would not release the file.',
          screenCaption: 'The result screen, as shown in the analysis',
          transectComplete: 'All {{total}} transects are listed.',
          transectTruncated:
            'Listing the first {{shown}} of {{total}} transects. The summary above covers all of them; the listing is for spot-checking.',
          forecastYears: '{{years}} years',
          common: {
            footer: 'CASSIE · Coastal Analysis via Satellite Imagery Engine',
            areaHeading: 'Study area',
            areaFile: 'Area file',
            areaExtent: 'Extent (lon, lat)',
            crs: 'Coordinate system',
            baselineVertices: 'Baseline vertices',
            baselineExtent: 'Baseline extent',
            imageryHeading: 'Imagery',
            satellite: 'Satellite',
            imageCount: 'Images used',
            imageList: 'Dates',
            coastlines: 'Extracted coastlines',
            resultHeading: 'Result',
            transectCount: 'Transects',
            measuredPeriod: 'Measured period',
            resultNote:
              'The rates are per transect. LRR comes from the linear regression, EPR from the end points; negative values mean erosion.',
            classHeading: 'Classification',
            classNote: "Esteves classification, applied to each transect's LRR rate.",
            transectHeading: 'Transects',
            mapsHeading: 'Map',
          },
          rates: {
            columns: { statistic: 'Statistic', mean: 'Mean', min: 'Minimum', max: 'Maximum', count: 'n' },
            names: {
              lrr: 'LRR (m/year)',
              epr: 'EPR (m/year)',
              nsm: 'NSM (m)',
              sce: 'SCE (m)',
              rsquared: 'Regression R²',
            },
          },
          classes: { columns: { className: 'Class', count: 'Transects', share: 'Share' } },
          transects: {
            columns: {
              id: 'Transect',
              lrr: 'LRR',
              epr: 'EPR',
              nsm: 'NSM',
              sce: 'SCE',
              rsquared: 'R²',
              className: 'Class',
            },
          },
          esteves: {
            Stable: 'Stable',
            Accreted: 'Accreted',
            Eroded: 'Eroded',
            'Critically Eroded': 'Critically eroded',
          },
          shoreline: { text: { title: 'Shoreline Evolution Report' } },
          shore_r: { text: { title: 'Shore-R Report' } },
          shore_s: {
            text: {
              title: 'Shore-S Report',
              errorHeading: 'Model error',
              errorNote:
                'Each number measures a different slice: the average covers every paired observation, the last 6 months cover the end of the series, and the final one covers the last date alone.',
              modeledCount: 'Modelled coastlines',
              referenceCount: 'Reference coastlines',
              pairedObservations: 'Paired observations',
              byDateHeading: 'Error over time',
              byDateNote: 'Shows whether the model drifts as it runs forward rather than being wrong evenly.',
              byTransectHeading: 'Error per transect',
              byTransectNote: 'Localises a poor fit to a stretch of coast instead of spreading it over the analysis.',
            },
          },
          errors: {
            rmseAvg: 'Average RMSE (m)',
            biasAvg: 'Average bias (m)',
            rmseLast6m: 'RMSE over the last 6 months (m)',
            rmseFinal: 'RMSE at the final date (m)',
            biasFinal: 'Bias at the final date (m)',
            rmsm: 'Measured RMS (m)',
            biasm: 'Measured bias (m)',
            bss: 'Brier Skill Score',
          },
          byDate: { columns: { date: 'Date', rms: 'RMS (m)', bias: 'Bias (m)' } },
          byTransect: { columns: { transect: 'Transect', rms: 'RMS (m)', bias: 'Bias (m)' } },
          mepbay: {
            text: {
              title: 'MEPBAY Report',
              imageDate: 'Image date',
              bayCount: 'Bays analysed',
              fitHeading: 'Parabolic fit',
              fitNote:
                'Coefficients of the parabolic equilibrium shape fitted to each bay, with the β angle the fit turns on.',
              controlHeading: 'Control points',
              controlNote: 'The three points that defined each curve, in the order they are marked.',
            },
          },
          bays: {
            columns: {
              bay: 'Bay',
              beta: 'β (degrees)',
              cosBeta: 'cos β',
              c0: 'C0',
              c1: 'C1',
              c2: 'C2',
              r0: 'R0 (m)',
            },
          },
          controlPoints: {
            columns: { bay: 'Bay', point: 'Point', longitude: 'Longitude', latitude: 'Latitude' },
            names: {
              diffractionPoint: 'Diffraction point',
              coastalControlPoint: 'Closure point',
              predominantWavePoint: 'Tangent point',
            },
          },
          intertidal: {
            text: {
              title: 'Intertidal Zone Report',
              tideFile: 'Tide file',
              waterlineHeading: 'Waterlines',
              waterlineCount: 'Waterlines',
              withTideCount: 'With a tide level',
              tideRange: 'Tide range',
              tideSpan: 'Vertical span',
              waterlineNote:
                'Each waterline is paired with the tide level at the moment of the image. The elevation model is interpolated within the span covered; outside it is extrapolation.',
              tableHeading: 'Waterlines and tides',
            },
            missingTideNote: '{{missing}} waterline(s) have no tide level. They do not enter the elevation model.',
            tideRangeValue: '{{low}} m to {{high}} m',
            tideSpanValue: '{{span}} m',
          },
          waterlines: { columns: { date: 'Date', tide: 'Tide (m)' } },
          csqueeze: {
            text: {
              title: 'Coastal Squeeze Report',
              sourceHeading: 'Land cover',
              collection: 'Collection',
              periodCount: 'Periods',
              periodList: 'Periods analysed',
              forecast: 'Forecast',
              coverHeading: 'Area by cover and period',
              coverNote: 'Summed over every transect. The per-transect detail is in the CSV export.',
              classHeading: 'Squeeze classification',
              classNote: 'How many transects have the coastal habitat pinned and how many still have room to retreat.',
              transectHeading: 'Transects',
            },
          },
          euscoast: {
            text: {
              title: 'EUSCoast Report',
              sourceHeading: 'Land cover',
              collection: 'Collection',
              periodCount: 'Periods',
              periodList: 'Periods analysed',
              forecast: 'Forecast',
              coverHeading: 'Area by cover and period',
              coverNote: 'Summed over every transect. The per-transect detail is in the CSV export.',
              classHeading: 'Squeeze classification',
              classNote: 'How many transects have the coastal habitat pinned and how many still have room to retreat.',
              transectHeading: 'Transects',
            },
          },
          cover: { columns: { period: 'Period' } },
        },
        loading: {
          kml: 'Reading the KML file…',
          area: 'Preparing the selected area…',
          cloudCoverage: 'Checking cloud cover over the area…',
          searchingImages: 'Looking for available images…',
          images: 'Loading the images…',
          years: 'Looking for years with imagery…',
          composite: 'Building the composite image…',
          trainingData: 'Reading the training data…',
          charts: 'Rebuilding the charts…',
          chartsProgress: 'Sampling the training points: {{done}} of {{total}} parts…',
          model: 'Reading the uploaded model…',
          map: 'Preparing the map…',
          result: 'Building the result…',
          depth: 'Computing the bathymetry…',
          waterlines: 'Loading the waterlines…',
          zones: 'Computing the intertidal zones…',
          export: 'Preparing the file for download…',
          depthZones: 'Vectorising the depth into bands…',
        },
        sessionHistory: {
          button: 'Saved analyses',
          title: 'Analyses saved in this browser',
          empty: 'No analyses saved yet.',
          unnamed: 'Unnamed analysis',
          opening: 'Opening the saved analysis…',
          missing: 'This analysis is no longer saved in this browser.',
          remove: 'Remove from the history',
          close: 'Close',
        },
        fileErrors: {
          selectFileFirst: 'Select a file first.',
          kmlNoCoordinates: 'No valid coordinates were found in the KML file.',
          unreadableFile: 'The selected file could not be read.',
          missingCoordinateSystem: 'Inform the coordinate system.',
          missingTrainingFile: 'Select the training data file.',
          missingDate: 'Inform the date before continuing.',
        },
        screenshot: {
          button: 'Save screen image (PNG)',
          error: 'Could not generate the screen image.',
        },
        overlayPanels: {
          hide: 'Hide panels',
          show: 'Show panels',
        },
        workflowStepper: {
          blocked: 'Finish the earlier steps to open this one',
        },
        stepSummary: {
          label: 'Acquisition summary',
          area: 'Area defined',
          images: {
            singular: '{{total}} image',
            plural: '{{total}} images',
          },
          periods: {
            singular: '{{total}} period',
            plural: '{{total}} periods',
          },
        },
        returnButtons: {
          returnToLastStep: 'Back to last step',
          returnToBegin: 'Return to module selection',
        },
      },
      intertidal: {
        analysesTitle: '{{index}} band Standard Deviation',
        useAnalysis: 'Use',
        usingAnalysis: 'Using',
        selected: 'Selected',
        threshold: 'Threshold',
        aweiLayers: '{{index}} layers',
        chooseIndexTitle: 'Index choice',
        zoneIndexLabel: 'Zone index',
        waterlineIndexLabel: 'Coastline index',
        waterlineProgress: '{{completed}} of {{total}} images processed…',
        createRaster: 'Create',
        selectAnalysisRequired: 'Please select an analysis before continuing.',
        editAnalysis: 'Edit threshold',
        deleteAnalysisConfirm: 'Do you want to delete "{{name}}"?',
        noVectorsFound: 'No vector found.',
        deleteAllPolygonsConfirm: 'You are about to delete all polygons. Continue?',
        indexAwei: 'AWEI',
        indexNdwi: 'NDWI',
        indexMndwi: 'MNDWI',
        indexMndwiSharp: 'MNDWI (sharpened)',
        hideAnalysis: 'Hide analysis',
        showAnalysis: 'Show analysis',
        title: 'Intertidal',
        prev: 'Return',
        next: 'Continue',
        exportData: 'Export data (EPSG 4326)',
        confirm: 'Confirm',
        year: 'Year',
        years: 'years',
        timezone: 'Time zone',
        selectValue: 'Select a value...',
        startDateMandatory: 'Start date is mandatory.',
        importTideDataFile: 'Select tide data file.',
        dataFormat: 'Data format.',
        downloadSampleTideData: 'Click to download tide data file sample.',
        column1: 'datetime',
        column2: 'tideData',
        doubleValue: 'Valor double',
        endDateMandatory: 'End date is mandatory.',
        endDateAfterStartDate: 'End date must be after the start date.',
        zoneOnlyLabel: 'Generate only the intertidal zone',
        zoneOnlyDescription:
          'Delimiting the intertidal zone does not depend on tide data — only the final topographic reconstruction does. Turning this on makes tide data optional and skips the coastline-extraction and Kriging interpolation steps.',
        optionalField: 'Optional',
        zoneOnlyResultNotice:
          '"Product 1 only" mode active: only the intertidal zone was delimited, without coastline extraction or Kriging interpolation. To download the zone raster, go back to step 4 and use "Download images".',
        tideDataPeriodMismatchTitle: 'Tide data incompatible with the selected period',
        tideDataPeriodMismatchMessage:
          'The imported tide file covers {{tideMin}} to {{tideMax}}, which does not overlap with the selected analysis period ({{periodStart}} to {{periodEnd}}). Adjust the analysis period or import a compatible tide file before continuing.',
        tideFileTooLarge: 'The file is too large ({{size}} MB). The limit is 5 MB.',
        readFileTide: 'Please select a file first.',
        lineTideFileErrorPartA: 'Problem with line in tide data file:',
        lineTideFileErrorPartB: '- do not contains the proper format.',
        tileDataProcessed: 'Tide file processed.',
        geeAccessError: 'Error accessing Google Earth Engine',
        mapnotready: 'The map is not ready!',
        kmlFileProcessed: 'KML file processed.',
        kmlNoCoordinates: 'No valid coordinates were found in the KML file.',
        interpretFileError: 'It was not possible to interpret the selected file.',
        cloudFilterMandatory: 'Cloud filter is mandatory.',
        attributes: {
          dateTime: 'Date-Hour',
          tideValue: 'Tide Level',
          dateImage: 'Image Date',
          dateGauge: 'Tide Gauge Date',
          noProperties: 'No properties',
        },
        downloadVectors: 'DOWNLOAD AREA (SHAPEFILE)',
        downloadImages: 'DOWNLOAD IMAGES (RASTER)',
        aweiZone: '{{index}} Band',
        waterlines: 'Lines',
        layer: 'Layer',
        legend: 'Legend',
        vectorEditor: 'Vector Editor - Review and Clean Detected Zones',
        clickPolygons: 'Click on the polygons to mark/unmark for deletion',
        deletedCount: '{{count}} polygon(s) marked for deletion (red)',
        noneDeleted: 'No polygon marked for deletion',
        skip: 'Skip Edition',
        description: 'Review and edit the detected intertidal zones by removing unwanted polygons',
        samplingInterval: 'Sampling Interval',
        generateKriging: 'Generate Kriging Interpolation',
        processingKriging: 'Processing interpolation...',
        downloadKriging: 'Download Kriging Result',
        krigingLegend: 'Tide Level (m)',
        krigingInterpolation: 'Kriging Interpolation',
        krigingStats: 'Interpolation Statistics',
        krigingPrepare: 'Prepare Interpolation',
        krigingStep1: 'Remove lines',
        krigingStep2: 'Select dates',
        krigingStep1Desc: 'Click on waterlines on the map to mark them for removal. Click again to unmark.',
        krigingStep2Desc: 'Select the dates to be used in the interpolation. Unchecked dates will be ignored.',
        linesMarked: 'line(s) marked for removal',
        noLinesMarked: 'No lines marked',
        confirmInterpolation: 'Confirm Interpolation',
        exportKrigingTiff: 'Export Kriging (.tiff)',
        krigingEmptyMaskError:
          'Could not generate the Kriging interpolation for the selected spectral index. Go back to the previous step and try a different index for the intertidal zone definition.',
        krigingMaskUnavailable:
          'Intertidal zone mask not available. Make sure the analysis was run in the previous step.',
        krigingNoWaterlines: 'No waterline available. Process the waterlines first.',
        krigingAoiNotFound: 'Area of interest not found.',
        krigingNoTideValues: 'No tide value found in the waterlines.',
        krigingVisualizationError:
          'Error generating visualization. The image may be too large. Try increasing the sampling interval.',
        krigingGenericError: 'Error running interpolation. Check the console for details.',
        krigingExportMissingData: 'Generate the Kriging interpolation before exporting.',
        krigingGeotiffGenerationError: 'Error generating GeoTIFF file. Check the console.',
        krigingGeotiffExportError: 'Error exporting GeoTIFF file.',
        generatingTiff: 'Generating GeoTIFF...',
        viewTideChart: 'View tide chart',
        tideChart: {
          dialogTitle: 'Tide level chart',
          seriesLabel: 'Tide series (Min = {{min}}, Max = {{max}})',
          acquisitionLabel: 'Satellite images ({{count}})',
          xAxisLabel: 'date',
          yAxisLabel: 'tide level [m]',
          exportButton: 'Export image',
          showFullTimeline: 'Show full timeline',
          showLimitedRange: 'Show {{days}} days before/after',
          noData: 'No tide data available.',
        },
        1: {
          title: 'Satellite selection',
          description: 'Choose one of the options to define the tide data.',
          dateIntervalComments:
            '3 months is the ideal period for analyses of the intertidal region. Shorter periods may result in too few images for analysis. Longer periods may mask morphological changes, which can affect the results of the topography in the intertidal zone.',
          card: {
            opticalResolution: 'Optical Resolution',
            opticalResolutionUnit: 'meters',
            activityPeriod: 'Activity Period',
            provider: 'Provider',
            revisitTime: 'Revisit time',
            revisitTimeUnit: 'days',
            choose: 'Choose',
          },
        },
        2: {
          title: 'Selection of area of interest and tide data',
          description: 'Select the area of interest, the period, and the tide data.',
          showDelimiters: ' delimitadores',
        },
        3: {
          title: 'Image aquisition',
          description: 'Filter the images for the collection, taking into account different tide levels.',
          loading: 'Loading...',
        },
        4: {
          title: 'Acceptence of identified area',
          description: 'Analyse the area identified as the intertidal zone.',
          loading: 'Loading...',
          period: 'Period',
          adjust: 'Adjust',
          histogramTitle: 'Standard Deviation Histogram of the {{index}} Band',
          histogramDescription:
            'Through the image histogram in terms of standard deviation, select the threshold that separates the intertidal zone from the water features. Higher values will tend to generate a smaller area.',
        },
        5: {
          title: 'Area Refinement...',
          description: 'Area refinement process...',
          cardText:
            'Considering that mangroves grow in areas that can be up to 10 meters above sea level, the compression calculation uses ground elevation filters, with this value adopted as the standard reference.',
          confirm: 'CONFIRM',
        },
        validationStep: {
          title: 'Intertidal area validation',
        },
        result: {
          steps: 'Steps',
          chartsTitle: 'Evolution Chart',
          cancel: 'Cancel',
        },
      },
      algorithm: {
        title: 'Module Selection',
        beta: 'Beta',
        mepbay: 'MepBay',
        mepbayDesc: 'Model for equilibrium planform of bay beaches.',
        csqueeze: 'Coastal Squeeze',
        csqueezeDesc: 'Mapping and analysis of indicators to obtain the Coastal Squeeze potential in mangrove areas.',
        euscoast: 'EUSCoast',
        euscoastDesc:
          'Identification and assessment of human intervention areas and urban evolution on the coastal zone, including the urban expansion potential over the analysed period.',
        bathymetry: 'Bathymetry',
        bathymetryDesc:
          'Estimation of shallow water depth on a selected coastal region, using optical inversion algorithms and machine learning.',
        shoreline: 'Shoreline Analyst',
        shorelineDesc:
          'Automatic mapping and statistical analysis of the shoreline evolution on any coastal area of interest in the world, using this module.',
        intermare: 'Intertidal',
        intermareDesc: 'Depth estimation on intertidal zones, through waterline method.',
        shore_r: 'Shoreline-R',
        shore_rDesc: 'Static Shoreline Forecasting based on tendency analysis over the selected time series.',
        shore_s: 'Shoreline-S',
        shore_sDesc: 'Validation and error assessment of shoreline sediment transport models.',
        disclaimerTitle: 'Disclaimer',
        disclaimerText:
          'By continuing, you acknowledge that the interpretation and use of the results generated by the tool are your responsibility and require adequate technical knowledge. The developers are not responsible for analyses and decisions made based on the results. Commercial use is subject to the rules and restrictions of Google Earth Engine (GEE) - see more at:',
        disclaimerReject: 'Reject',
        disclaimerAccept: 'Accept',
      },
      acquisition: {
        title: 'Image Acquisition',
        prev: 'Return',
        next: 'Continue',
        sessionImport: {
          button: 'Import test session',
          wrongModule: 'This file is not an exported session from this module.',
          error: 'Could not import the file. Check whether it is a valid session.',
          notAuthenticated:
            'You need to be signed in to Google Earth Engine to import this session. Please sign in and try again.',
          loading: 'Importing session...',
        },
        sessionExport: {
          button: 'Export test session',
        },
        pdfReport: {
          tooltip:
            'Download a PDF report with the whole analysis: area, imagery, model, metrics and maps in a single document',
        },
        geoJsonExport: {
          tooltip:
            "Download this analysis's layers (area, baseline, transects, results) as GeoJSON, to open in a GIS such as QGIS",
          empty: 'This analysis has no vector layers to export yet.',
        },
        periodAndImages: {
          title: 'Period and images',
          description:
            'Set the period and cloud filter, search for images and choose which ones to use. You can adjust the period and search again as many times as you like, without leaving this step.',
        },
        1: {
          title: 'Choose satellite',
          description: 'Select one of the available satellites for image acquisition.',
          card: {
            opticalResolution: 'Optical Resolution',
            opticalResolutionUnit: 'meters',
            activityPeriod: 'Activity Period',
            provider: 'Provider',
            revisitTime: 'Revisit time',
            revisitTimeUnit: 'days',
            about: {
              'Sentinel-2': 'High resolution and revisit frequency, ideal for coastal analysis.',
              Landsat: 'Long historical series, ideal for temporal analyses.',
            },
            choose: 'Choose',
          },
          notice: {
            title: 'Which satellite to choose?',
            sentinel:
              'Sentinel-2 sees finer detail: each pixel covers 10 m and a new image arrives every 5 days. Pick it when detail is what matters - narrow beaches, spits, inlets - or when the analysis is about recent years.',
            landsat:
              'Landsat sees further back: each pixel covers 30 m and a new image arrives every 16 days, but the series starts in 1984. Pick it when the record is what matters - decade-long trends, the before and after of coastal engineering.',
            combined:
              'Both together: the analysis uses images from both satellites, which adds a good deal more dates to work with. Best when neither one alone has enough usable images, keeping in mind that the detail varies with each image (10 m for Sentinel-2, 30 m for Landsat).',
            tip: 'When in doubt: detail calls for Sentinel-2, history calls for Landsat. Over cloudy regions, widen the search period with either one - the number of usable images falls well below the revisit cycle.',
          },
        },
        2: {
          title: 'Set region of interest',
          description: 'Draw the region of interest using the map below.',
          showDelimiters: 'Show delimiters',
          undo: 'Undo',
          close: 'Close',
          processFile: 'Process file',
          importLegend: 'Import the area of interest from a KML file:',
          importTitle: 'KML import',
          noKmlFileSelected: 'No file selected',
          example: 'Examples may be downloaded here:',
          dataTrainning: 'Data Training',
          dataTrainningLegend: 'Import training data',
          dataTrainningFileLabel: 'Please inform the date of tranning file:',
          dataTrainningDateHint:
            '*Use file name ending with _ddmmyyyy.csv to have the training date field automatically filled.',
          bathymetryDescription: 'Please define the Area of Interest and the training data file to continue.',
          labelBathymetryStep: 'Select the area of interest and the training data',
          helpCoordinateSystem:
            'By default CASSIE utilizes the EPSG:4326 coordinates system.\nBut in case your training file is based on another EPSG coordinates system,\nyou may type the coordinates system code of your file that CASSIE will interpret your data.',
          helpCoordinateSystemTitle: 'Coordinate System',
          dataTrainningDateHintTitle: 'Training Date',
          checkboxIntertidal: 'Possible tide areas',
          referencePossibleTideGauge: 'About the "Possible Intertidal Areas" Layer',
          possibleTideGaugeButtonToltip:
            'This button enables the global intertidal zone layer (UQ_murray Intertidal v1.1), a worldwide map of intertidal areas generated from remote sensing data and modeling.',
          references: 'References:',
          seeLayerReference: 'See reference layer',
          intertidalArea: 'Draw the area of interest on the map or import a KML file.',
          intertidalPeriod: 'Period',
          intertidalTideData: 'Tide data',
          intertidalTideImportKmz: 'Import kml',
          intertidalDrawOnMap: 'Draw on map',
          intertidalDrawOnMapDetails: 'Use the buttons on the map to define the area of interest.',
          intertidalTideImportData: 'Import data',
          intertidalTideExtractData: 'Extract data from model',
          intertidalPeriodSelection: 'Define the period of interest',
          intertidalPeriodStartDate: 'Select the analysis period start date',
          intertidalPeriodEndDate: 'Select the analysis period end date',
          intertidalSelectStartAndEndDate: 'Select the start and end dates for the analysis period:',
          intertidalCloudFilter: 'Define the maximum cloud coverage: ',
          intertidalConfirmPeriod: 'Confirm period',
          intertidalSelectAreaTitle: 'Choose one of the following options to define the area',
          intertidalSelectTideTitle: 'Choose one of the following options to define the tide data',
          bathymetry: {
            waterMaskMethod: 'Choose one earth mask method',
            waterMaskThreshold: 'Inform the threshold value (between -1 and 1)',
            trainingDataReducer: 'Select the reducer for the training data',
            bringingModel: 'I will load a trained model',
            bringingModelHelp:
              'Allows moving on without a soundings CSV. Use it when the intent is to apply a saved model to this imagery rather than fit a new one. The model is loaded in the algorithms step.',
            validationSplit: 'Hold points back for validation',
            validationSplitHelp:
              'Fit the model on {{train}}% of the training points and measure the error on the {{validation}}% it never saw. Off, the error is measured on the same points the model was fitted on.',
          },
          bathymetryAoiChooser: {
            inputFilesAndSettings: 'Input files and settings',
            inputFilesAndSettingsDescription:
              'Import the KML file of the area of interest, upload the training CSV, review the coordinate system, and set the processing options.',
            aoiFileTitle: 'Area of interest file (.kml)',
            importKml: 'Import KML',
            noKmlFileSelected: 'No KML file selected.',
            aoiLoadedOnMap: 'Area of interest loaded on map.',
            drawOnMapOrImportKml: 'Draw on the map or import a KML file.',
            trainingDataTitle: 'Training data (.csv)',
            importCsv: 'Import CSV',
            noCsvFileSelected: 'No CSV file selected.',
            fileLabel: 'File:',
            pointsLabel: 'Points:',
            trainingDateTitle: 'Training date',
            trainingDatePlaceholder: 'dd/mm/yyyy',
            coordinateSystemTitle: 'Coordinate system',
            coordinateSystemExample: 'Example: EPSG:4326',
            coordinateSystemLookup: 'Find coordinate system values here:',
            trainingDataReducerFallback: 'Training data reducer',
            trainingPlotError:
              'The training points could not be drawn on the map. Check the coordinate system you entered, and that you are signed in to Earth Engine.',
            thresholdPlaceholder: '0.124',
            confirmTrainingData: 'Confirm training data',
            areaOfInterestExample: 'Area of interest (.kml)',
            trainingDataFile4326: 'Training data file (.csv) - EPSG:4326',
            trainingDataFile32722: 'Training data file (.csv) - EPSG:32722',
            coeficientsTitle: 'Add coeficients (optional): ',
            coeficientsDescription:
              'If there are coeficients added, the imported training data will be utilized to fill the "or_depth" column and for the metrics calculation.',
            helpTrainingFileTitle: 'Input file format:',
            helpTrainingFileFormat: 'Format: Comma-separated values (.csv)',
            helpTrainingFileColumns: 'Columns: 3',
            helpTrainingFileOrder: 'Order: X, Y, Z',
            helpTrainingFileDecimal: 'Decimal: period (.)',
            pressEscToClose: 'Press [ESC] to close.',
            confirmTrainingDataValidation:
              'Please select the training data file, inform the date and the coordinate system before continuing.',
            geeSignupError:
              'Your account is still not allowed to use the GEE API. You should complete your registration using this link: https://earthengine.google.com/signup/',

            timezoneTitle: 'Time zone',
            invalidTideFile: 'Please ensure it is a CSV file with the correct format and try again.',
            noTideFileSelected: 'No tide data file selected. Please select a file to continue.',

            tideFileFormat:
              'Please ensure it is a CSV file with two columns: "datetime" (in ISO 8601 format) and "tideData" (numeric values).',
            importTideCsv: 'Import tide data (.csv)',
            tideDataTitle: 'Tide data file (.csv)',
            timezoneHelper: 'Example: -3 for Brasília (BRT), 0 for UTC',
            tideTitle: 'Tide correction',
            tideDatetimeField: 'Datetime field',
            tideLevelField: 'Level field',
            tideDatetimeFormat: 'Datetime format',
            tideMaxDiff: 'Maximum difference (minutes)',
            tideCompositeReducer: 'Composite reducer',
            tideApplyCorrection: 'Apply depth correction',
            tideHint: 'Matching and chart will be shown in the next step after images are loaded.',
            tideCsvFile: 'Import tide data (.csv)',
            timezoneOffset: 'Timezone offset (hours)',
            tideDateColumn: 'Date column',
            tideValueColumn: 'Tide value column',
            tideWarningTitle: 'Tide correction enabled',
            tideWarningText:
              'You enabled tide correction, but no tide CSV file has been imported yet. To continue, import the file or proceed without correction.',
            tideWarningContinueWithout: 'Continue without',
            tideWarningImport: 'Import',
            or: 'or',
          },
        },
        3: {
          title: 'Set period',
          description: 'Use the time slider to specify the period of analysis.',
          startDate: 'Start date',
          endDate: 'End date',
          warning: 'The end date cannot be earlier than the start date',
          languageTag: 'english',
          loading: 'Loading...',
          period: 'Period',
          to: 'to',
          durationDays: {
            singular: 'day',
            plural: 'days',
          },
          durationMonths: {
            singular: 'month',
            plural: 'months',
          },
          durationYears: {
            singular: 'year',
            plural: 'years',
          },
          imageQuantity: {
            singular: 'image',
            plural: 'images',
          },
          cloudPercentage: 'Cloud percentage',
          labelBathymetryStep: 'Period selection',
          contentBathymetryStep: 'Please select the desired period to continue.',
          loadingCloudCoverage: 'Loading cloud coverage...',
          loadingAvailableDates: 'Loading available dates...',
          searchImages: 'Search images',
          noImagesFound: 'No images found for the selected period.',
          noImagesMatchCloudFilter:
            'Images were found for the selected period, but none match the current cloud filter. Try raising the cloud level.',
          bathymetry: {
            selectIntervalTitle: 'or select a range',
            selectIntervalDescription: 'Click to generate a composite image from a date range',
            selectPeriodButton: 'Select period',
            genericError: 'An unexpected error occurred. Please try again.',
            compositeError:
              'Unable to generate the composite image. The processing exceeded the memory limit or the selected parameters are too heavy. Try reducing the date range and generate again.',
            compositeReducer: 'Choose the composite reducer',
            description: 'Specify the image to be utilized:',
            imageDate: 'Image date',
            dataDate: 'In-situ data date',
            downloadImage: 'Download image',
            intervalSelection: 'Click here to select a interval for median image.',
            medianImage: 'Median image',
            intervalDefinition: 'Interval Definition',
            geomedWarning:
              'GEOMED_EPS should be a small positive value used only for numerical stability. Typical safe values range between 1e-6 and 1e-3. Higher values (e.g., ~1e-3) improve stability but may smooth the data, while lower values (e.g., ~1e-6 or smaller) preserve more detail but can introduce noise or instability.',
            geomedPerformance:
              'Geometric Median Geomed is the most computationally expensive reducer. Avoid using it with large image collections. It is recommended to use time ranges of up to 3 months.',
            generateImage: 'Generate image',
            invalidGeomedEps: 'Please enter a valid GEOMED_EPS value greater than 0.',
            noImagesForPeriod: 'No images were found for the selected period and area.',
            filteredCollectionNoBands: 'The filtered collection returned an image with no bands.',
            generatedCompositeNoBands: 'The generated composite image has no bands.',
            compositeGenerateError:
              'An error occurred while generating the composite image. Please adjust the parameters and try again.',
            compositeRetry: 'Try again',
            geomedEpsInvalidHelp: 'Enter a valid positive number. Examples: 1e-3, 1e-4, 0.001',
            geomedEpsHelp: 'Used as the minimum value before log(): img.toFloat().max(GEOMED_EPS).log()',
            compositePrepareError: 'An error occurred while preparing the composite image. Please try again.',
            errorTimeout: 'The operation timed out. Try reducing the selected period.',
            errorQuota: 'Request limit reached. Please wait a few minutes and try again.',
            errorPermission: 'You do not have permission to access this resource on Google Earth Engine.',
            errorNotRegistered: 'Account not registered with Google Earth Engine.',
            errorMemory: 'The area or period is too large to process. Reduce the region or date range.',
            errorRequestSize:
              'The data sent to Earth Engine went over its 48 MB limit per request. The size comes from the files loaded, not from the area: reduce the number of points in the training CSV or of vertices in the KML.',
            treeErrorMemory:
              'The model’s sampling and validation settings were too heavy for Earth Engine. Lower the samples per band, the number of folds, or switch to the Ratios predictor set.',
            geoJsonDepthFailed:
              'The depth could not be vectorised. The file was written with the area and the training points, without the bands.',
            errorNetwork: 'Connection error with the server. Check your internet and try again.',
            gaussianKernelPreview: 'Gaussian Kernel Preview',
            gaussianKernelPreviewDescription: 'Preview visual of the kernel with radius and sigma.',
            parameters: 'Parameters',
            radius: 'Radius',
            sigma: 'Sigma',
            step1Title: 'Period Selection',
            imagesInPeriod: 'Images available in the period',
            loadImagesButton: 'Load Images',
            step2Title: 'Image List',
            step3Title: 'Generate Composite Image',
            periodChanged: 'The period has been changed. Load the images again to update the list.',
            refreshTable: 'Click to refresh the table.',
            loadingImages: 'Loading images...',
            generatingImage: 'Generating image...',
            images: 'images',
            imagesSelected: 'Selected images: ',
            smoothingVeryLight: 'Very light smoothing',
            smoothingModerate: 'Moderate smoothing',
            smoothingStrong: 'Strong smoothing',
            smoothingVeryStrong: 'Very strong smoothing',
            smoothRadius: 'Radius',
            smoothSigma: 'Sigma',
            kernelLabel: 'Kernel size',
          },
        },
        4: {
          title: 'Filter images',
          description: 'Filter images to keep the appropriate images only.',
          table: {
            id: 'ID',
            cloud: 'Clouds',
            viewMode: 'View mode',
            viewGrid: 'Grid view',
            viewList: 'List view',
            thumbnail: 'Thumbnail',
            tide: 'Tide',
            selected: 'Selected',
            noData: 'No data available',
          },
          imagesPerPage: 'Images per page',
          to: 'to',
          of: 'of',
          importDates: 'Import dates (CSV)',
          importResult: '{{matched}} of {{total}} dates found and selected.',
          importEmpty: 'None of the dates in the file match the images in this list.',
          importInvalid: 'No valid date was found in the file. Expected columns: name, date.',
          importError: 'The file could not be read.',
          labelBathymetryStep: 'Bands, algorithm and threshold selection',
          contentBathymetryStep: 'Please select the bands, the algorithm and the threshold to be applied',
          next: 'Complete',
          attention: 'ATTENTION:',
          attentionMessage:
            'Depending on the selected date range and area of interest, the satellite image may appear incomplete due to dataset availability in the Google Earth Engine. You may choose to work only with complete scenes or, alternatively, use the visible portions to extract additional shorelines, just remember to account for these limitations when interpreting coastal evolution.',
          reloadFullList: 'Reload every image found before selection',
          bathymetry: {
            modelSelection: 'Select a training model',
            algorithmSelection: 'Select an algorithm',
            algorithmSelectionHint:
              'Click over a chart to select an algorithm. The selected one become with red borders.',
            thresholdSelection: 'Light extinction threshold (meters)',
            algorithmName1: 'Band ratio',
            bandsSelection: 'Bands selection',
            updateCharts: 'Update charts',
            modelStore: {
              load: 'Load a trained model',
              loadHelp:
                'Applies a model saved in another analysis instead of fitting a new one. Useful for running the same rules over imagery from other dates.',
              loaded:
                'Using the {{model}} model trained on {{date}} with {{samples}} points. Fitting will be skipped in this analysis.',
              notJson: 'The file is not valid JSON.',
              notAModel: 'This file is not a CASSIE bathymetry model.',
              version: 'This model was saved by a different version of CASSIE and cannot be read.',
              unknownModel: 'The saved model is not Random Forest, Gradient Boost or Ensemble.',
              noPredictors: 'The saved model does not say which predictors it was trained on.',
              noTrees: 'The saved model does not contain the trained trees.',
              mismatch:
                'The model was trained on {{expected}} imagery, but {{actual}} is selected now. The bands share their names, so the model would answer anyway, from the wrong data.',
              missingPredictors:
                'The model needs predictors these settings do not produce: {{names}}. Switch the predictor set to the one it was trained with.',
            },
            treeCalibration: 'Model calibration',
            treeCalibrationHelp:
              'Tree models produce no coefficients: what you tune are the hyperparameters below. The defaults are the reference run\'s; change one at a time and compare the RMSE by depth band on the result page.',
            treeRfTrees: 'RF: trees',
            treeRfVarsPerSplit: 'RF: vars per split',
            treeRfMinLeaf: 'RF: min. per leaf',
            treeRfBagFraction: 'RF: bag fraction',
            treeGbtTrees: 'GBT: trees',
            treeGbtShrinkage: 'GBT: shrinkage',
            treeGbtSamplingRate: 'GBT: sampling',
            treeGbtMaxNodes: 'GBT: max nodes',
            treeGbtLoss: 'GBT: loss',
            treeEnsembleWeight: 'RF weight',
            treeVarsMode: { P3: 'n/3 (regression)', SQRT: 'sqrt(n)' },
            treePredictors: 'Predictors',
            treePredictorsHelp:
              'Full is the reference configuration: every optical band, SPM02, the pSDB ratios and the 3x3 neighbourhood statistics. The narrower sets are there to isolate where an error comes from. Logarithms are deliberately absent: a tree splits on a threshold, so log(B) is the same partition as B.',
            treeValidation: 'Validation',
            treeValidationHelp:
              'Folds of ground, not of points: two pixels thirty metres apart see almost the same water over almost the same bottom, so a random split measures the model on a neighbour of what it was fitted on. Larger blocks are more pessimistic and more honest.',
            treeCvScheme: 'Cross-validation',
            treeCvFolds: 'CV: folds',
            treeCvBlock: 'CV: block (m)',
            treeStratifyBin: 'Strata band (m)',
            treeSamplesPerBin: 'Samples per band',
            treeEnsembleWeighting: 'Ensemble weighting',
            treeCvSchemes: { BLOCK: 'Spatial blocks', RANDOM: 'Random', NONE: 'Off' },
            treeWeightings: { OPTIMAL: 'Optimal (out-of-fold)', INV_MSE: 'By inverse error', EQUAL: 'Equal halves' },
            treePredictorsFull: 'Full',
            treePredictorsBands: 'Bands',
            treePredictorsRatios: 'Bands + ratios',
            band: 'Band',
            errorTitle: 'Error',
          },
        },
        5: {
          bathymetry: {
            bathymetry: 'Bathymetry',
            metricsReport: 'Metrics Report',
            loadStatistics: 'Click here to load statistics',
            returnToBegin: 'Return to module selection',
            meters: 'meters',
            minDepth: 'Minimum Predicted Depth',
            maxDepth: 'Maximum Predicted Depth',
            processing: 'Processing...<br />It may take a few seconds depending the tranning data size.',
            downloadStatistics: 'Download statistics',
            legendTitle: 'Satellite Derived Bathymetry - Depth (m)',
            shorelineAnalysis: 'Shoreline Analysis',
            applyMosaic: 'Apply mosaic to the image',
            pdfReport: {
              title: 'Bathymetry Report',
              subtitle: 'CASSIE · {{model}} · generated {{date}}',
              footer: 'CASSIE · Coastal Analysis via Satellite Imagery Engine',
              missingImage: 'The image "{{caption}}" could not be included: the browser would not release the file.',
              failed: 'The PDF report could not be generated.',
              areaHeading: 'Study area',
              areaFile: 'Area file',
              areaExtent: 'Extent (lon, lat)',
              crs: 'Coordinate system',
              soundingsFile: 'Soundings file',
              soundings: 'Soundings',
              surveyedRange: 'Surveyed depth',
              surveyDate: 'Survey date',
              imageryHeading: 'Imagery',
              mission: 'Satellite',
              resolution: 'Optical resolution',
              processing: 'Processing',
              period: 'Period',
              waterMask: 'Water mask',
              waterThreshold: 'Mask threshold',
              imageCount: 'Images in the composite',
              imageList: 'Dates used',
              modelHeading: 'Model',
              model: 'Model',
              bands: 'Bands',
              extinctionDepth: 'Light extinction depth',
              trainingReducer: 'Soundings reducer',
              validationSplit: '70/30 validation',
              validationSplitOn: 'on — the metrics come from the held-out 30%',
              validationSplitOff: 'off — the metrics come from the same points the model was fitted on',
              predictorSet: 'Predictor set',
              seed: 'Seed',
              presetNote:
                'The coefficients were entered by hand, not fitted in this analysis. The metrics measure how they perform on this imagery.',
              metricsHeading: 'Metrics',
              metricsNote:
                'The metrics compare predicted against surveyed depth, pixel by pixel, inside the analysed area.',
              binsHeading: 'Error by depth band',
              binsNote:
                'Bands of 2 m over the surveyed depth. A band with few points says more about the survey than about the model.',
              depthCoarsened:
                'The area has {{requested}} pixels with a depth, above the file limit. The points were sampled every {{scale}} m instead of at the satellite’s resolution, covering the whole area with {{written}} points rather than cutting it short.',
              overallRmse: 'Overall mean RMSE',
              warningsHeading: 'Attention',
              warningsNote:
                'None of these invalidate the result, but they change how far it can be trusted. Check them before quoting the numbers.',
              staleWarning:
                'The training bathymetry is from {{survey}} and the imagery from {{image}}, {{years}} years later. The model assumes the sea bed the survey measured is the sea bed the image sees; in a mobile coastal system that gap may be read as model error.',
              thinWarning:
                'Bands with fewer than {{limit}} points: {{bands}}. The error there says more about the sample size than about the model.',
              binChart: {
                xLabel: 'Depth (m)',
                yLabel: 'RMSE (m)',
                caption:
                  'RMSE per 2 m band, at each band\'s midpoint. Where the curve is low and flat is where the model is reliable.',
              },
              binBand: 'Band',
              binCount: 'n',
              binRmse: 'RMSE (m)',
              binMae: 'MAE (m)',
              binBias: 'Bias (m)',
              mapsHeading: 'Maps',
              depthCaption: 'Depth estimated by the model',
              imageCaption: 'True-colour working image',
            },
            treeCvDegenerate:
              'The training points all fall inside a single {{block}} m block, so there is no way to separate folds in space. The model was fitted and the map is valid, but without a cross-validated error. Lower the block size below the extent of the survey.',
            modelStore: {
              save: 'Save the trained model',
              saving: 'Saving the model…',
              nothingToSave: 'No model was trained in this analysis. Run one of the tree models first.',
            },
            treeNoSamples:
              'No training point survived the predictor selection. A point only counts where every predictor has a value, and SPM02 and the neighbourhood statistics have none over part of the scene. Try the Ratios set, or widen the cut line.',
            treeNeedsTraining:
              'The tree models learn the depth from the training points, so they cannot run without them. Go back and import a training CSV, or pick one of the analytic models.',
            presetMismatch:
              'The coefficients you entered were fitted on {{expected}} imagery, but {{actual}} is selected now. Go back and select the same imagery, or enter coefficients fitted on this one.',
            viewTideChart: 'Tide chart',
            exportSessionState: 'Export session for testing',
            tideChart: {
              title: 'Tide level chart',
              dialogTitle: 'Tide level chart',
              seriesLabel: 'Tide series (Min = {{min}}, Max = {{max}})',
              acquisitionLabel: 'Image acquisition (Mean Interval: {{days}} days)',
              chartTitle: 'Tide levels at the time of image acquisition',
              yAxisLabel: 'tide level [m]',
            },
            bathymetryProcessing: {
              generateCsv: 'Generate CSV - Bathymetry',
              generateMetricsCsv: 'Generate metrics CSV',
              csvExportSplitWarning:
                'The selected area has ~{{pixels}} pixels, above the {{limit}} limit per export. It will be automatically split into {{parts}} parts and processed sequentially.',
              csvExportProgress: 'Processing area {{current}} of {{total}}...',
              downloadingImages: 'Downloading images...',
              downloadGeotiff: 'Download GeoTIFF',
              generateChart: 'Generate chart',
              metricsReport: 'Metrics Report',
              errorByDepth: 'RMSE by depth band',
              disableImage: 'Disable RGB image',
              hideDepthImage: 'Hide depth image',
              showDepthImage: 'Show depth image',
              enableImage: 'Enable RGB image',
              metrics: {
                BDS_MODEL: 'Model',
                MISSION: 'Mission',
                IMAGE_SOURCE: 'Image Source',
                RUN_MODE: 'Run Mode',
                SCALE_M: 'Scale (m)',
                CRS: 'CRS',
                targetDate: 'Target Date',
                n: 'Sample Count',
                rmse: 'RMSE',
                mae: 'MAE',
                bias: 'Bias',
                r2: 'R²',
                SI: 'Scatter Index',
                nRMSE: 'nRMSE',
                skill: 'Skill Score',
                tideLevel_m: 'Tide Level (m)',
                dateGauge: 'Gauge Date',
                dateImage: 'Image Date',
                TRAININGDATA_REDUCER: 'Training Reducer',
                VALIDATION_SPLIT: 'Train/validation split (%)',
                TREE_ENSEMBLES: 'Ensembles',
                TREE_PREDICTOR_SET: 'Predictor set',
                TREE_PREDICTOR_COUNT: 'Predictors (count)',
                MODEL_SOURCE: 'Model source',
                MODEL_TRAINED_ON: 'Trained on',
                MODEL_TRAINED_SAMPLES: 'Points in the original training',
                TREE_N_SAMPLES: 'Training points',
                TREE_PREDICTORS: 'Predictors',
                TREE_RF_N_TREES: 'RF trees',
                TREE_RF_VARS_PER_SPLIT: 'RF vars per split',
                TREE_RF_MIN_LEAF: 'RF min. per leaf',
                TREE_RF_BAG_FRACTION: 'RF bag fraction',
                TREE_GBT_N_TREES: 'GBT trees',
                TREE_GBT_SHRINKAGE: 'GBT shrinkage',
                TREE_GBT_SAMPLING_RATE: 'GBT sampling rate',
                TREE_GBT_MAX_NODES: 'GBT max nodes',
                TREE_GBT_LOSS: 'GBT loss',
                TREE_ENSEMBLE_W_RF: 'Ensemble RF weight',
                CV_SCHEME: 'CV scheme',
                CV_FOLDS: 'CV folds',
                CV_BLOCK_M: 'CV block (m)',
                CV_N: 'CV samples',
                CV_FOLDS_ASKED: 'CV folds requested',
                CV_DEGENERATE: 'CV: block not divisible',
                CV_RMSE: 'CV RMSE',
                CV_MAE: 'CV MAE',
                CV_BIAS: 'CV bias',
                CV_R2: 'CV R²',
              },
            },
          },
        },
      },
      map: {
        roi: 'Area of interest',
        baseline: 'Baseline',
        shorelines: 'Shorelines',
        showAll: 'Show all',
        transects: {
          title: 'Transects',
          legend: 'Color legend',
          stable: 'Stable',
          accreted: 'Accreted',
          eroded: 'Eroded',
          criticallyEroded: 'Critically Eroded',
          biasPositive: 'Positive bias',
          biasNegative: 'Negative bias',
          biasNoData: 'No paired data',
        },
        item: {
          s: 'item',
          p: 'items',
        },
        cancel: 'Cancel',
        edit: 'Edit',
        finishEdit: 'Confirm',
        editTooFewPoints: 'The area needs at least 3 points.',
        editSelfIntersection: 'The area cannot cross over itself. Adjust the points before continuing.',
        editOutsideAOI: 'The baseline must stay inside the area of interest.',
      },
      imageChooser: {
        title: 'Map images available',
        resultQuantity: 'images available',
        helpTooltip: 'Images available',
        helpContent:
          'This section shows the satellite images that were previously selected during the date selection step.',
        image: 'Image',
        load: 'Load',
        exportDates: 'Export dates (CSV)',
        actions: {
          title: 'Actions',
          analyzeShoreline: {
            title: 'Analyze Shoreline',
            imageSelection: {
              title: 'Select imagery',
              cancel: 'Cancel',
              confirm: 'Confirm',
            },
            baselineDraw: 'Draw baseline',
          },
        },
      },
      shorelineParameters: {
        title: 'Input parameters',
        description:
          'Define the transect spacing and extension, in meters, and input the threshold for land-water segmentation (0 = to automatic detection using Otsu thresholding method, -1 = automatic detection using multi-Otsu thresholding method',
        spacing: 'Spacing (m)',
        extension: 'Extension (m)',
        threshold: 'Threshold',
        cancel: 'Cancel',
        confirm: 'Confirm',
      },
      shoreR: {
        moduleName: 'Shoreline-R',
        analysis: {
          title: 'Shoreline-R',
          execute: 'Confirm and Start Analysis',
          step: '3. Run Analysis',
        },
        timeSeriesHint: {
          title: 'ATTENTION:',
          message:
            'Keep in mind that shoreline projections based on historical coastlines generally benefit from longer time series.',
        },
        periodHint: {
          title: 'ATTENTION:',
          message:
            'Specify the start and end dates for the image dataset. Keep in mind that the average of the images from the last year of the time series can be used as a reference for the projection.',
        },
        baseline: {
          title: '1. Baseline',
          draw: 'Draw baseline',
          confirm: 'Confirm baseline',
        },
        transects: {
          title: '2. Transects',
          confirm: 'Confirm transects',
        },
        projection: {
          legendLabel: 'Projection',
          title: '4. Configure Projection',
          horizonLabel: 'Temporal horizon (years)',
          customYearPlaceholder: 'Other',
          addCustomYear: 'Add',
          maxHorizonsWarning: 'Maximum of {{max}} periods reached',
          methodLabel: 'Extrapolation method',
          methods: {
            smoothed_linear_regression: 'Linear Regression',
            smoothed_end_point_rate: 'End Point Rate',
          },
          referenceShorelineLabel: 'Reference shoreline',
          referenceShorelineModes: {
            latest: 'Most recent (default)',
            byDate: 'Pick by date',
            yearlyMean: 'Last-year mean',
          },
          referenceShorelineDateLabel: 'Date',
          confirm: 'Confirm configuration',
          yearUnit: 'years',
        },
        bruun: {
          openButton: 'Future Sea Level Adjustment',
          title: 'Future Sea Level Adjustment (Bruun Rule)',
          horizonLabel: 'Projection horizon',
          slraLabel: 'SLRa (mm/yr)',
          lLabel: 'L - Distance to closure (m)',
          hStarLabel: 'h* - Closure depth (m)',
          dcLabel: 'DC - Beach/dune height (m)',
          confirm: 'Apply Adjustment',
          undo: 'Undo Adjustment',
          legendLabel: 'Bruun',
          formulaLabel: 'Formula',
          helpTooltip: 'About the Bruun Rule',
          helpContent:
            'The Bruun Rule estimates the horizontal shoreline retreat caused by sea level rise. The calculation uses the SLR rate, the time horizon, and beach profile dimensions (closure distance, closure depth, and beach/dune height).',
        },
        redoLastStep: 'Redo Projection',
        redoAnalysis: 'Redo Analysis',
        outlierDialog: {
          title: 'Outlier',
          description:
            'The following have distances outside median ± 3σ on at least 1/5 of the transects. Check the dates you would like to remove and the analysis will be re-run without them.',
          confirm: 'Continue',
        },
        exports: {
          openButton: 'Export',
          title: 'Export ShoreR Analysis',
          close: 'Close',
          empty: 'Select at least one item.',
          formatLabel: 'Format',
          export: 'Export',
          nonGeoref: {
            title: 'Non-georeferenced',
            coastlineAnalysis: 'Coastline analysis',
            rates: 'Calculated rates per transect',
            extrapolations: 'Calculated extrapolations per transect',
          },
          georef: {
            title: 'Georeferenced',
            shorelines: 'Coastlines',
            reference: 'Reference line',
            baseline: 'Baseline',
            future: 'Future lines',
            transects: 'Transects',
          },
        },
      },
      shorelineAnalysis: {
        title: 'Shoreline Analysis',
        navSubtitle: 'Coastal analysis and monitoring',
        moduleName: 'Shoreline',
        navMapTab: 'Map',
        layersPanelTitle: 'Layers',
        backgroundMapTitle: 'Background map',
        mapTypeMap: 'Map',
        mapTypeSatellite: 'Satellite',
        mapTypeLabels: 'Labels',
        mapTypeTerrain: 'Terrain',
        mapTypeTerrainUnavailable: 'Google provides no relief layer over satellite imagery',
        mapTypeDark: 'Dark',
        toggleTheme: 'Toggle light/dark mode',
        toggleThemeUnavailable: 'Not available on this page',
        baselineAOIDrawAlert: 'Baseline must be fully inside the Area of Interest (AOI).',
        transectsSettings: {
          defineTransects: 'Define the amount of transects that will be drawn on the baseline:',
          setStepLabel: 'Distance between transects in meters',
          setExtentLabel: 'Transect length in meters',
          importTransectsStepsExtent: 'Import transects to obtain distance and extent(.kml)',
          or: 'or:',
          importBaseline: 'Importar BaseLine (.kml)',
          setThresholdLabel: 'Input the Threshold',
          confirm: 'Confirm and Start Analysis',
          alert: {
            importTransectQuantity: 'The file must contain at least two transects.',
          },
        },
        transectsReport: {
          title: 'Transects Report',
          headers: {
            id: 'ID',
            initialLatitude: 'Latitude Begin',
            initialLongitude: 'Longitude Begin',
            finalLatitude: 'Latitude End',
            finalLongitude: 'Longitude End',
            initialDate: 'Date Begin',
            finalDate: 'Date End',
            intercept: 'Intercept',
            slope: 'Slope',
            rsquared: 'r²',
            lrr: 'LRR',
            sce: 'SCE',
            nsm: 'NSM',
            epr: 'EPR',
            label: 'Class',
          },
        },
        process: {
          extraction: 'Extracting shorelines',
          estimate: 'ETC',
        },
        exports: {
          title: 'Export Data',
          csv: 'Export CSV',
          json: 'Export JSON',
          shp: 'Export Shapefile',
          images: 'Export Images',
          kml: 'Export KML',
          sessionState: 'Export session for testing',
        },
        restartAnalysis: 'Restart Analysis',
        close: 'Close',
        toggleLayers: {
          on: 'Turn on layers',
          off: 'Turn off layers',
        },
        errors: {
          invalidTransectError: 'Could not calculate shorelines.',
        },
      },
      transectEvolution: {
        chartLanguage: 'English',
        title: 'Transect Evolution',
        TransectEvolutionDialog:
          'Information about shoreline evolution analysis over time, including linear regression, daily, monthly and yearly change rates, coefficient of determination (R²), standard deviation, uncertainties, and outlier detection.',
        TransectEvolutionInfoDialog:
          'To begin the analysis, you must select the desired satellite images, draw the baseline along the coast, and configure the transects. Transect configuration includes setting the spacing between transects, the length of each transect, and the threshold value used to filter the data considered in the analysis. These parameters directly affect the accuracy and sensitivity of the results.',
        MeanTransectDialog:
          'This chart displays the average shoreline position over time based on available transect data; for each date, it gathers shoreline distances from all transects with data, calculates the mean of those distances, and produces a time series that helps visualize overall trends of shoreline advance or retreat without focusing on individual transect variations.',
        statistics: 'Statistics',
        slope: 'Slope - Rate of change per day',
        intercept: 'Intercept - Starting position of regression line',
        lrr: 'LRR - Linear Regression Rate',
        r: 'r - Correlation Coefficient',
        sce: 'SCE - Shoreline Change Envelope',
        nsm: 'NSM - Net Shoreline Movement',
        epr: 'EPR - End Point Rate',
        eprUnc: 'EPRunc - Uncertainty of End Point Rate',
        lse: 'LSE - Standard Error of Regression',
        lci: 'LCI - Confidence Interval of Regression',
        meanTransect: 'Mean Transect Position',
        averageBeachProfile: 'Mean Beach Profile',
        label: 'Classification',
        units: {
          meters: 'm',
          mByYr: 'm/yr',
          mByMonth: 'm/month',
          metersPerDay: 'm/day',
        },
        labels: {
          x: 'Year',
          y: 'Distance to Baseline (m)',
          trend: 'Trend',
        },
        outliers: 'Remove Outliers: ',
        deviation: 'Standard Deviation: ',
        downloadchart: 'Download chart',
        transectList: 'Transect Filter: ',
        exportChart: 'Export Chart',
        chartTitle: 'Shoreline Evolution - Transect',
        noData: 'No Data',
        averagePosition: 'Average shoreline position over time',
      },
      hovmoller: {
        hovmollerInfoDialog:
          'The Hovmöller plot represents the variation of the average shoreline position over time for each transect, allowing the identification of patterns of advance (accretion) or retreat (erosion) across different periods.',
        hovmollerPlot: 'Hovmöller Plot',
        popupTitle: 'Hovmöller Plot',
        xAxisLabel: 'Time',
        yAxisLabel: 'Transect',
        colorbarTitle: 'Distance from Average Position (m)',
        colorbarLabels: {
          low: 'Receding / Erosion',
          mid: 'Average Position',
          high: 'Advancing / Accretion',
        },
        close: 'Close',
      },
      imageryOverlay: {
        base: 'Base',
        hint: 'New layer',
        loading: 'Loading',
        noLayers: 'No layers on this image yet.',
        plotBathymetry: 'Click here to plot Bathymetry image over the map.',
        plotMedian: 'Click here to plot Median image over the map.',
        bathymetryLoad: "Click on 'Create' to display the Bathymetry Layer and wait the process be concluded.",
        MedianLoad: "Click on 'Create' to display the Median Layer and wait the process be concluded.",
        add: {
          title: 'New layer',
          name: 'Layer name',
          expression: 'Expression',
          bands: {
            title: 'Available Bands',
            red: 'RED: Red band',
            green: 'GREEN: Green band',
            blue: 'BLUE: Blue band',
            nir: 'NIR: Near infrared band',
            swir: 'SWIR: Shorwave infrared band',
          },
          suggested: 'Expressions',
          create: 'Create',
        },
      },
      csqueeze: {
        moduleName: 'Coastal Squeeze',
        title: 'Coastal Squeeze',
        prev: 'Back',
        next: 'Next',
        year: 'Year',
        years: 'years',
        landcovers: {
          mangrove: 'Mangrove',
          vegetation: 'Vegetation',
          water: 'Water',
          human: 'Human intervention',
          beach: 'Beach, Dune and Sand Spot',
          intertidal: 'Intertidal',
        },
        1: {
          title: 'Choose satellite',
          description: 'Select one of the available satellites for image acquisition.',
          card: {
            opticalResolution: 'Optical Resolution',
            opticalResolutionUnit: 'meters',
            activityPeriod: 'Activity Period',
            provider: 'Provider',
            revisitTime: 'Revisit time',
            revisitTimeUnit: 'days',
            choose: 'Choose',
          },
        },
        2: {
          title: 'Set region of interest',
          description: 'Draw the region of interest using the map below.',
          showDelimiters: 'Show delimiters',
          undo: 'Undo',
          processFile: 'Process file',
          importLegend: 'Import the area of interest from a KML file:',
          importTitle: 'KML import',
          aoiFileTitle: 'Area of interest',
          fileLabel: 'File:',
          noKmlFileSelected: 'No KML file selected.',
          aoiLoadedOnMap: 'Area of interest loaded on the map.',
          drawOnMapOrImportKml: 'Draw on the map or import a KML file.',
          example: 'Examples may be downloaded here:',
          dataTrainning: 'Data Training',
          dataTrainningLegend: 'Import training data',
          dataTrainningFileLabel: 'Please inform the date of training file:',
          dataTrainningDateHint:
            '*Use file name ending with _ddmmyyyy.csv to have the training date field automatically filled.',
          bathymetryDescription: 'Please define the Area of Interest and the training data file to continue.',
          labelBathymetryStep: 'Select the area of interest and the training data',
          helpCoordinateSystem:
            'By default CASSIE utilizes the EPSG:4326 coordinates system.\nBut in case your training file is based on another EPSG coordinates system,\nyou may type the coordinates system code of your file that CASSIE will interpret your data.',
          performanceMessage: 'The marked area is {number} km² and will perform {level} during calculations.',
          slow: 'slow',
          moderate: 'moderate',
          fast: 'fast',
          viewMangrove: 'Possible mangrove areas',
          geeError: {
            title: 'Error Notice!',
            content:
              'It seems that your account is not yet authorized to use the Google Earth Engine tools. You need to complete your registration through this link: ',
            close: 'Close',
          },
          errorTitle: 'Error message',
          errorMessageSelectFile: 'Please select a file first.',
          errorMessageDrawingOutline: 'Error drawing outline.',
          errorMessageNoValidCoord: 'No valid coordinates were found in the KML file.',
          errorMessageUnableInterpretKml: 'Unable to interpret KML file.',
          infoTitle: 'Informative message',
          infoMessageSuccess: 'KML file processed successfully.',
          close: 'Close',
        },
        3: {
          title: 'Image Selection - 1',
          description:
            'View the images and add them to the list.\n If more than one image is selected per period, a final median image will be generated.\n A cloud removal filter will be applied to images with clouds.',
          loading: 'Loading...',
          period: 'Period',
          to: 'to',
          durationDays: {
            singular: 'day',
            plural: 'days',
          },
          durationMonths: {
            singular: 'month',
            plural: 'months',
          },
          durationYears: {
            singular: 'year',
            plural: 'years',
          },
          imageQuantity: {
            singular: 'image',
            plural: 'images',
          },
          cloudPercentage: 'Cloud percentage',
        },
        4: {
          title: 'Image Selection - 2',
          description:
            'View the images and add them to the list.\n If more than one image is selected per period, a final median image will be generated.\n A cloud removal filter will be applied to images with clouds.',
          period: 'Period',
          to: 'to',
          with: 'with',
          tabText: 'Select the available dates in the bar below by clicking on the pink dots:',
          emptyImages: 'No images found with this percentage of clouds.',
          viewImage: 'View image',
          listText: '*Select at least 1 image from this year for processing.',
          addImage: 'ADD IMAGE',
          discard: 'CANCEL',
          alreadyAdded: 'ALREADY ADDED',
          table: {
            id: 'ID',
            cloud: 'Clouds',
            thumbnail: 'Thumbnail',
            remove: 'Remove',
          },
          durationDays: {
            singular: 'day',
            plural: 'days',
          },
          durationMonths: {
            singular: 'month',
            plural: 'months',
          },
          durationYears: {
            singular: 'year',
            plural: 'years',
          },
          imageQuantity: {
            singular: 'image',
            plural: 'images',
          },
          selected: 'selected',
          cloudPercentage: 'of cloud percentage',
          nextWarning: 'Select 1 image from each year to continue',
        },
        5: {
          title: 'Classify the land cover',
          description:
            'Draw regions of land cover types on the map or, if available, select a data source with land cover information.',
          cardText:
            'Considering that the mangrove grows in areas that can be up to 10 meters above sea level, the compression calculation uses soil elevation filters, with this value being adopted as a standard reference.',
          confirm: 'CONFIRM',
          undo: 'Remove all',
          example: 'Example',
          elevation: 'Elevation',
          withElevation: 'Elevation of 10 meters',
          noElevation: 'No elevation filter',
          defineElevation: 'To set an elevation value other than the default change the field below:',
          labels: 'Labels',
          labelsInfo: '*Numbers correspond to landcover code.',
          availableDataset: 'AVAILABLE DATASET',
          availableDatasetText: 'This year has a collection of soil data mapped by',
          apply: 'Apply',
          defineLandCovers: 'SELECT TYPE TO DRAW ON MAP',
          removePolygon: 'Remove shape',
          export: 'Export',
          import: 'Import',
          importPolygons: 'IMPORT POLYGONS COORDINATES',
          formatImport: 'CSV FORMAT: landcover code; lat1,lng1|lat2,lng2|...',
          spanImport: 'After processing the file, the polygons automatically appear on the map',
          toContinue: '*Draw polygons for at least two different classes to generate the classification mesh.',
          generate: 'GENERATE RASTER',
          generateFailed: 'The classification raster could not be generated. Please try again.',
          confirmRaster: 'CONFIRM THE RASTER GENERATED',
          confirmRasterText:
            'Check the generated raster on the map and, if you are satisfied, check that the rasters of both years are completed before releasing the "CONTINUE" button.',
          editPolygons: 'EDIT POLYGONS',
          nextStepAlert: '*In the next step it will no longer be possible to change dates, images or the raster.',
          falseColor: 'False color',
          shortcut: 'SHORTCUT: You can move the map by keeping the CTRL button pressed.',
          nextWarning: 'Generate the two year raster to continue',
          mapBiomasAlertTitle: 'Confirm MapBiomas Dataset Application',
          mapBiomasAlertText:
            "By confirming this action, the MapBiomas dataset will be applied to the map and the raster will be generated. All previously drawn polygons will be discarded. You can delete the MapBiomas classification and draw new polygons at any time by clicking the 'Edit Polygons' button.",
          alertDialogConfirm: 'Confirm',
          alertDialogCancel: 'Cancel',
          alertDialogClose: 'Close',
          polygonsFileProcessed: 'Processed and imported polygon file',
          drawingMode: 'Drawing Mode',
          drawingModeText: 'Click on the map to draw rectangles of the type ',
          drawingModeRemove: 'Remove Mode',
          drawingModeTextRemove: 'Click on the rectangle to remove it',
        },
        result: {
          steps: 'Steps',
          polygons: 'Polygons',
          baseLine: 'Baseline',
          drawBaseline: 'Draw baseline',
          transect: 'Transect',
          transects: 'Transects',
          visibilityController: 'Visibility Controller',
          visibilityControllerDesc: '*More recent year layers overlay older year layers.',
          visibilityControllerinfo:
            'In this panel, you can view the selected satellite images and the land-use classification raster generated in the previous step by toggling each layer with the “eye” icon. The slider allows you to adjust layer transparency. You can also export all images used or generated in the previous step.',
          configTransects: 'Configure transects',
          calcule: 'Start analysis',
          stepsInfo:
            'To perform the temporal-change analysis and future projection, you must configure the analysis transects. First, draw a baseline from which the transects will be generated. Then, set their spacing and length. Don’t forget to click Start Analysis to run the processing.',
          tipBaseline: 'To end the drawing you must double-click on the map at the end point of the line.',
          confirmBaseline: 'Do you want to commit the line or redo?',
          confirm: 'Confirm',
          cancel: 'Cancel',
          redo: 'Redo',
          defineTransects: 'Define the amount of transects that will be drawn on the baseline:',
          quantityTransects: 'Distance between transects (in meters)',
          lengthTransects: 'Size (for each side in meters)',
          chartsTitle: 'Evolution charts',
          transectResult: 'Transect Result',
          transectResultInfo:
            'This panel shows how the area of each land-cover class changes for the selected transect, across the two analysis years and the chosen forecast. To switch transects, change the number at the top of the panel or pick one directly on the map.',
          loading: {
            wait: '*The process can take a few minutes.',
            preparing: 'Preparing data...',
            failed:
              'The transect analysis could not be started. Check that the area and the classified images have loaded, then try again.',
          },
          in: 'in',
          output: {
            noMangrove: 'There is not enough mangrove for the calculation of this transect.',
            noHuman: 'There is not enough human intervention for the calculation of this transect.',
            thisTransect: 'in this transect.',
            noSqueeze: 'No squeeze',
            squeeze: 'Potential of {number}% to squeeze',
            fullSqueeze: 'Squeeze potential is greater than 100%',
            organizeYear: 'Organizing data from {year}...',
            calculatingTransect: 'Calculating transect',
            calculatingComplete: 'Calculation of {year} finished...',
            ready: 'Ready',
          },
          forecast: 'Forecast',
          colorLegend: 'Color legend',
          legend: {
            fullSqueeze: 'Full squeeze',
            moderateSqueeze: 'Moderate squeeze',
            noSqueeze: 'Stable mangrove',
            noMangrove: 'Insufficient mangrove',
          },
          restart: 'Restart',
          export: 'Export CSV file',
          shp: 'Export Shapefile',
          exportInfo:
            'Click to export the CSV file containing land-cover area data for all three analysis years (the same data displayed in the previous panel).',
          shpInfo:
            'Click to export the Shapefile files containing the baseline and the transects generated by the analysis.',
          id: 'ID',
          labels: {
            id: 'ID',
            0: 'MGV',
            1: 'VEG',
            2: 'HUM',
            3: 'WTR',
            4: 'BCH',
          },
        },
      },
      euscoast: {
        moduleName: 'EUSCoast',
        title: 'EUSCoast',
        prev: 'Back',
        next: 'Next',
        year: 'Year',
        years: 'years',
        landcovers: {
          mangrove: 'Mangrove',
          vegetation: 'Vegetation',
          water: 'Water',
          human: 'Human intervention',
          beach: 'Beach, Dune and Sand Spot',
          intertidal: 'Intertidal',
        },
        1: {
          title: 'Choose satellite',
          description: 'Select one of the available satellites for image acquisition.',
          card: {
            opticalResolution: 'Optical Resolution',
            opticalResolutionUnit: 'meters',
            activityPeriod: 'Activity Period',
            provider: 'Provider',
            revisitTime: 'Revisit time',
            revisitTimeUnit: 'days',
            choose: 'Choose',
          },
        },
        2: {
          title: 'Set region of interest',
          description: 'Draw the region of interest using the map below.',
          showDelimiters: 'Show delimiters',
          undo: 'Undo',
          processFile: 'Process file',
          importLegend: 'Import the area of interest from a KML file:',
          importTitle: 'KML import',
          aoiFileTitle: 'Area of interest',
          fileLabel: 'File:',
          noKmlFileSelected: 'No KML file selected.',
          aoiLoadedOnMap: 'Area of interest loaded on the map.',
          drawOnMapOrImportKml: 'Draw on the map or import a KML file.',
          example: 'Examples may be downloaded here:',
          dataTrainning: 'Data Training',
          dataTrainningLegend: 'Import training data',
          dataTrainningFileLabel: 'Please inform the date of training file:',
          dataTrainningDateHint:
            '*Use file name ending with _ddmmyyyy.csv to have the training date field automatically filled.',
          bathymetryDescription: 'Please define the Area of Interest and the training data file to continue.',
          labelBathymetryStep: 'Select the area of interest and the training data',
          helpCoordinateSystem:
            'By default CASSIE utilizes the EPSG:4326 coordinates system.\nBut in case your training file is based on another EPSG coordinates system,\nyou may type the coordinates system code of your file that CASSIE will interpret your data.',
          performanceMessage: 'The marked area is {number} km² and will perform {level} during calculations.',
          slow: 'slow',
          moderate: 'moderate',
          fast: 'fast',
          geeError: {
            title: 'Error Notice!',
            content:
              'It seems that your account is not yet authorized to use the Google Earth Engine tools. You need to complete your registration through this link: ',
            close: 'Close',
          },
          errorTitle: 'Error message',
          errorMessageSelectFile: 'Please select a file first.',
          errorMessageDrawingOutline: 'Error drawing outline.',
          errorMessageNoValidCoord: 'No valid coordinates were found in the KML file.',
          errorMessageUnableInterpretKml: 'Unable to interpret KML file.',
          infoTitle: 'Informative message',
          infoMessageSuccess: 'KML file processed successfully.',
          close: 'Close',
        },
        3: {
          title: 'Image Selection - 1',
          description:
            'View the images and add them to the list.\n If more than one image is selected per period, a final median image will be generated.\n A cloud removal filter will be applied to images with clouds.',
          loading: 'Loading...',
          loading: 'Loading...',
          period: 'Period',
          to: 'to',
          durationDays: {
            singular: 'day',
            plural: 'days',
          },
          durationMonths: {
            singular: 'month',
            plural: 'months',
          },
          durationYears: {
            singular: 'year',
            plural: 'years',
          },
          imageQuantity: {
            singular: 'image',
            plural: 'images',
          },
          cloudPercentage: 'Cloud percentage',
        },
        4: {
          title: 'Image Selection - 2',
          description:
            'View the images and add them to the list.\n If more than one image is selected per period, a final median image will be generated.\n A cloud removal filter will be applied to images with clouds.',
          loading: 'Loading...',
          period: 'Period',
          to: 'to',
          with: 'with',
          tabText: 'Select the available dates in the bar below by clicking on the pink dots:',
          emptyImages: 'No images found with this percentage of clouds.',
          viewImage: 'View image',
          listText: '*Select at least 1 image from this year for processing.',
          addImage: 'ADD IMAGE',
          discard: 'CANCEL',
          alreadyAdded: 'ALREADY ADDED',
          table: {
            id: 'ID',
            cloud: 'Clouds',
            thumbnail: 'Thumbnail',
            remove: 'Remove',
          },
          durationDays: {
            singular: 'day',
            plural: 'days',
          },
          durationMonths: {
            singular: 'month',
            plural: 'months',
          },
          durationYears: {
            singular: 'year',
            plural: 'years',
          },
          imageQuantity: {
            singular: 'image',
            plural: 'images',
          },
          selected: 'selected',
          cloudPercentage: 'of cloud percentage',
          nextWarning: 'Select 1 image from each year to continue',
        },
        5: {
          title: 'Classify the land cover',
          description:
            'Draw regions of land cover types on the map or, if available, select a data source with land cover information.',
          confirm: 'CONFIRM',
          undo: 'Remove all',
          example: 'Example',
          labels: 'Labels',
          labelsInfo: '*Numbers correspond to landcover code.',
          availableDataset: 'AVAILABLE DATASET',
          availableDatasetText: 'This year has a collection of soil data mapped by',
          apply: 'Apply',
          defineLandCovers: 'SELECT TYPE TO DRAW ON MAP',
          removePolygon: 'Remove shape',
          export: 'Export',
          import: 'Import',
          importPolygons: 'IMPORT POLYGONS COORDINATES',
          formatImport: 'CSV FORMAT: landcover code; lat1,lng1|lat2,lng2|...',
          spanImport: 'After processing the file, the polygons automatically appear on the map',
          toContinue: '*Draw polygons for at least two different classes to generate the classification mesh.',
          generate: 'GENERATE RASTER',
          generateFailed: 'The classification raster could not be generated. Please try again.',
          confirmRaster: 'CONFIRM THE RASTER GENERATED',
          confirmRasterText:
            'Check the generated raster on the map and, if you are satisfied, check that the rasters of both years are completed before releasing the "CONTINUE" button.',
          editPolygons: 'EDIT POLYGONS',
          nextStepAlert: '*In the next step it will no longer be possible to change dates, images or the raster.',
          falseColor: 'False color',
          shortcut: 'SHORTCUT: You can move the map by keeping the CTRL button pressed.',
          nextWarning: 'Generate the two year raster to continue',
          mapBiomasAlertTitle: 'Confirm MapBiomas Dataset Application',
          mapBiomasAlertText:
            "By confirming this action, the MapBiomas dataset will be applied to the map and the raster will be generated. All previously drawn polygons will be discarded. You can delete the MapBiomas classification and draw new polygons at any time by clicking the 'Edit Polygons' button.",
          alertDialogConfirm: 'Confirm',
          alertDialogCancel: 'Cancel',
          alertDialogClose: 'Close',
          polygonsFileProcessed: 'Processed and imported polygon file',
          drawingMode: 'Drawing Mode',
          drawingModeText: 'Click on the map to draw rectangles of the type ',
          drawingModeRemove: 'Remove Mode',
          drawingModeTextRemove: 'Click on the rectangle to remove it',
        },
        result: {
          returnToLastStep: 'Back to last step',
          returnToBegin: 'Return to module selection',
          steps: 'Steps',
          polygons: 'Polygons',
          baseLine: 'Baseline',
          drawBaseline: 'Draw baseline',
          transect: 'Transect',
          transects: 'Transects',
          visibilityController: 'Visibility Controller',
          visibilityControllerDesc: '*More recent year layers overlay older year layers.',
          visibilityControllerinfo:
            'In this panel, you can view the selected satellite images and the land-use classification raster generated in the previous step by toggling each layer with the “eye” icon. The slider allows you to adjust layer transparency. You can also export all images used or generated in the previous step.',
          configTransects: 'Configure transects',
          calcule: 'Start analysis',
          stepsInfo:
            'To perform the temporal-change analysis and future projection, you must configure the analysis transects. First, draw a baseline from which the transects will be generated. Then, set their spacing and length. Don’t forget to click Start Analysis to run the processing.',
          tipBaseline: 'To end the drawing you must double-click on the map at the end point of the line.',
          confirmBaseline: 'Do you want to commit the line or redo?',
          confirm: 'Confirm',
          cancel: 'Cancel',
          redo: 'Redo',
          defineTransects: 'Define the amount of transects that will be drawn on the baseline:',
          quantityTransects: 'Distance between transects (in meters)',
          lengthTransects: 'Size (for each side in meters)',
          chartsTitle: 'Evolution charts',
          transectResult: 'Transect Result',
          transectResultInfo:
            'This panel shows how the area of each land-cover class changes for the selected transect, across the two analysis years and the chosen forecast. To switch transects, change the number at the top of the panel or pick one directly on the map.',
          loading: {
            wait: '*The process can take a few minutes.',
            preparing: 'Preparing data...',
            failed:
              'The transect analysis could not be started. Check that the area and the classified images have loaded, then try again.',
          },
          in: 'in',
          output: {
            noUrban: 'There is not enough urban area for the calculation of this transect.',
            thisTransect: 'in this transect.',
            stable: 'No urban growth expected',
            moderateGrowth: 'Potential of {number}% urban expansion',
            advancedGrowth: 'Potential of {number}% urban expansion (advanced growth)',
            organizeYear: 'Organizing data from {year}...',
            calculatingTransect: 'Calculating transect',
            calculatingComplete: 'Calculation of {year} finished...',
            ready: 'Ready',
          },
          forecast: 'Forecast',
          colorLegend: 'Color legend',
          legend: {
            advancedGrowth: 'Advanced growth',
            moderateGrowth: 'Moderate growth',
            stable: 'Stable urban area',
            noUrban: 'Insufficient urban area',
          },
          restart: 'Restart',
          export: 'Export CSV file',
          shp: 'Export Shapefile',
          exportInfo:
            'Click to export the CSV file containing land-cover area data for all three analysis years (the same data displayed in the previous panel).',
          shpInfo:
            'Click to export the Shapefile files containing the baseline and the transects generated by the analysis.',
          vector: 'Export raster as vector',
          vectorInfo:
            'Click to export the vectorized land-cover classification (one polygon Shapefile per year, carrying the year, code and name of each class), ready to be opened in QGIS.',
          id: 'ID',
          labels: {
            id: 'ID',
            0: 'MGV',
            1: 'VEG',
            2: 'HUM',
            3: 'WTR',
            4: 'BCH',
          },
        },
      },
      mepbay: {
        moduleName: 'MepBay',
        title: 'MepBay',
        prev: 'Back',
        next: 'Continue',
        year: 'Year',
        years: 'years',
        periodAndImages: {
          title: 'Period and images',
          description:
            'Adjust the period and cloud filter and choose which images to use. You can readjust the period as many times as you like, without leaving this step.',
        },

        1: {
          title: 'Define the area of interest',
          description: 'Delimit the area of interest using the map below.',
          showDelimiters: ' delimiters',
          undo: 'Undo',
          processFile: 'Process file',
          importLegend: 'Import the area of interest using a KML file:',
          importTitle: 'Import KML',
          bathymetryDescription: 'Define the area of interest and the training file to continue.',
          labelBathymetryStep: 'Define the area of interest and training data',
          helpCoordinateSystem:
            'By default, CASSIE interprets coordinates using the EPSG:4326 system (WGS84 - Geographic Coordinates).\nBut if your training file is based on another EPSG coordinate system,\nyou can enter your system code, and CASSIE will interpret the data accordingly.',
          performanceMessage:
            'The marked area has {number} km² and will have a {level} performance during the calculations.',
          slow: 'slow',
          moderate: 'moderate',
          fast: 'fast',

          geeError: {
            title: 'Error Warning!',
            content:
              'It seems your account is not yet authorized to use Google Earth Engine tools. You need to complete your registration through this link: ',
            close: 'Close',
          },
          errorTitle: 'Error message',
          errorMessageSelectFile: 'Please select a file first.',
          errorMessageDrawingOutline: 'Please select a file first.',
          errorMessageNoValidCoord: 'No valid coordinates were found in the KML file.',
          errorMessageUnableInterpretKml: 'Unable to interpret the KML file.',
          infoTitle: 'Information message',
          infoMessageSuccess: 'KML file processed successfully.',
          close: 'Close',
        },

        2: {
          title: 'Define the periods',
          description: 'Select two years to obtain the images',
          loading: 'Loading...',
          period: 'Period',
          to: 'to',
          durationDays: {
            singular: 'day',
            plural: 'days',
          },
          durationMonths: {
            singular: 'month',
            plural: 'months',
          },
          durationYears: {
            singular: 'year',
            plural: 'years',
          },
          imageQuantity: {
            singular: 'image',
            plural: 'images',
          },
          cloudPercentage: 'Cloud level',
        },

        3: {
          title: 'Select the images',
          description:
            'View the images and add them to the list. \n (Images with clouds will have a removal filter applied)',
          loading: 'Loading...',
          period: 'Period',
          to: 'to',
          with: 'with',
          tabText: 'Select the available dates in the bar by clicking on the pink dots below:',
          emptyImages: 'No image found with this cloud percentage.',
          viewImage: 'View image',
          listText: '*Select at least 1 image from this year for processing.',
          addImage: 'ADD IMAGE',
          discard: 'DISCARD',
          alreadyAdded: 'ALREADY ADDED',
          table: {
            id: 'ID',
            cloud: 'Clouds',
            thumbnail: 'Thumbnail',
            remove: 'Remove',
          },
          durationDays: {
            singular: 'day',
            plural: 'days',
          },
          durationMonths: {
            singular: 'month',
            plural: 'months',
          },
          durationYears: {
            singular: 'year',
            plural: 'years',
          },
          imageQuantity: {
            singular: 'image',
            plural: 'images',
          },
          selected: 'selected',
          cloudPercentage: 'of cloud level',
          undo: 'Clear points',
          nextWarning: 'Select 1 image from each year to continue',
        },

        4: {
          title: 'Classify land cover',
          helpTooltip: 'Click to view help about layers',
          helpTitle: 'How to classify land cover',
          helpContent:
            'To generate the parabolic model, you must first create a new layer. This layer will store the control points and the resulting curve. Once the layer is created, follow these three steps: 1. Diffraction Point – This is the point where the wave begins to bend around the coastline. 2. Closure Point – This represents the end of the coastline segment being analyzed. 3. Tangent Point – This defines the predominant wave direction and helps shape the curve. Click each step in order and follow the instructions to select the correct location. Once all three points are marked, the model will be calculated automatically.',
          description:
            'Draw the regions of land cover types on the map or, if available, select a data source with land cover information.',
          cardText:
            'Considering that mangroves grow in areas that can be up to 10 meters above sea level, the compression calculation uses ground elevation filters, with this value adopted as the default reference.',
          confirm: 'CONFIRM',
          undo: 'Clear',
          example: 'Example',
          elevation: 'Elevation',
          withElevation: '10-meter elevation',
          noElevation: 'No elevation filter',
          defineElevation: 'To set a different elevation value from the default, change the field below',
          labels: 'Legend',
          labelsInfo: '*Numbers correspond to the land cover type code.',
          availableDataset: 'AVAILABLE DATASET',
          availableDatasetText: 'This year has a soil data collection mapped by',
          apply: 'Apply',
          defineLandCovers: 'SELECT THE TYPE TO DRAW ON THE MAP',
          removePolygon: 'Remove shape',
          export: 'Export',
          import: 'Import',
          importPolygons: 'IMPORT POLYGON COORDINATES',
          formatImport: 'CSV FORMAT: land cover code; lat1,lng1|lat2,lng2|...',
          spanImport: 'After processing the file, the polygons will automatically appear on the map',
          toContinue: '*Draw polygons for at least two different classes to generate the classification grid.',
          generate: 'GENERATE RASTER',
          confirmRaster: 'CONFIRM GENERATED RASTER',
          confirmRasterText:
            'Check the generated raster on the map. If satisfied, and both year rasters are completed, the "CONTINUE" button will be enabled.',
          editPolygons: 'EDIT POLYGONS',
          nextStepAlert:
            '*In the next step it will no longer be possible to go back to change dates, images, or the raster.',
          falseColor: 'False color',
          shortcut: 'SHORTCUT: You can move the map by holding the CTRL button.',
          nextWarning: 'Generate the raster for both years to continue',
          mapBiomasAlertTitle: 'Confirm Application of MapBiomas Dataset',
          mapBiomasAlertText:
            'By confirming this action, the MapBiomas dataset will be applied on the map and the raster will be generated. All previously drawn polygons will be discarded. You can erase the MapBiomas classification and draw new polygons at any time by clicking the "Edit Polygons" button.',
          alertDialogConfirm: 'Confirm',
          alertDialogCancel: 'Cancel',
          alertDialogClose: 'Close',
          polygonsFileProcessed: 'Polygons file processed and imported',
          drawingMode: 'Drawing Mode',
          drawingModeText: 'Click on the map to draw rectangles of type ',
          drawingModeRemove: 'Removal Mode',
          drawingModeTextRemove: 'Click the rectangle to remove it',
        },

        result: {
          layers: 'View Layers',
          steps: 'Steps',
          polygons: 'Polygons',
          baseLine: 'Baseline',
          drawBaseline: 'Draw baseline',
          transect: 'Transect',
          transectoinfo:
            'The color of the transects represents the estimated level of mangrove compression. Select the number of years for generating the forecast, based on the land-cover classificationThe color of the transects represents the estimated level of mangrove compression.',
          transects: 'Transects',
          visibilityController: 'Visibility controller',
          visibilityControllerDesc: "*The most recent year's layers overlap the older year's layers.",
          configTransects: 'Configure transects',
          calcule: 'Start analysis',
          tipBaseline: 'To finish the drawing, you must double-click on the map at the endpoint of the line.',
          confirmBaseline: 'Do you want to confirm the line or redraw it?',
          confirm: 'Confirm',
          cancel: 'Cancel',
          redo: 'Redo',
          defineTransects: 'Define the number of transects to be drawn from the baseline:',
          quantityTransects: 'Quantity (approximate)',
          lengthTransects: 'Size (for each side in meters)',
          chartsTitle: 'Evolution chart',
          transectResult: 'Transect Result',
          transectResultInfo:
            '2.	This panel displays the evolution of land-cover areas for the selected transect, using the two analysis years and the chosen forecast. To switch transects, change the number at the top of the panel or select one directly on the map.',
          loading: {
            wait: '*The process may take a few minutes.',
            preparing: 'Preparing data...',
          },
          in: 'in',
          output: {
            thisTransect: 'in this transect.',
            organizeYear: 'Organizing {year} data...',
            calculatingTransect: 'Calculating transect',
            calculatingComplete: '{year} completed...',
            ready: 'Analysis complete',
          },
          forecast: 'Forecast',
          restart: 'Restart',
          export: 'Export CSV file',
          shp: 'Export Shapefile',
          exportSessionState: 'Export session for testing',
          labels: {
            id: 'ID',
            0: 'MGV',
            1: 'VEG',
            2: 'HUM',
            3: 'WTR',
          },
          // MepBay specific translations
          layerName: 'Layer',
          auxiliaryPoint: 'Auxiliary Point',
          diffractionPoint: 'Diffraction Point',
          closurePoint: 'Closure Point',
          tangentPoint: 'Tangent Point',
          selectDiffractionPoint: 'Select the diffraction point (geometry2)',
          selectClosurePoint: 'Select the beach closure point (geometry3)',
          selectTangentPoint: 'Select the point tangent to the coastline (geometry4)',
          manageLayers: 'Manage Layers',
          viewAndExportData: 'Vizualize Selected Maps',
          viewAndExportDataInfo:
            'This area displays the satellite images that were previously selected during the date selection step.',
          noLayersAvailable: 'No layers available',
          dataAvailable: 'Data available',
          incomplete: 'Incomplete',
          viewDetailsAndExport: 'View Details and Export',
          createNewLayer: 'Create New Layer',
          createNewLayerInfo:
            'Creating a new layer is the first step in starting the parabolic modeling process. This layer acts as a dedicated space to store control points and the results generated during the workflow. Keeping data organized in separate layers ensures greater clarity, control, and flexibility in coastal analysis.',
          activeLayerInfo: 'Active Layer Information',
          completed: 'Completed',
          selecting: 'Selecting...',
          available: 'Available',
          blocked: 'Blocked',
          resetActiveLayer: 'Reset Active Layer',
          chooseLayerColor: 'Choose Layer Color',
          detailsAndExport: 'Details and Export',
          exportSatelliteImages: 'Export satellite images',
          coefficientsAndParameters: 'Coefficients and Parameters',
          betaAngle: 'Beta Angle',
          thetaRadiusTable: 'Theta and Radius Table',
          theta: 'Theta',
          radius: 'Radius',
          noLineFound: 'No line found in active layer for export.',
          couldNotCalculateBounds: 'Could not calculate area limits for export.',
          errorExportingTiff: 'Error exporting map as TIFF:',
          errorDetails: 'Error details:',
          errorExportingImage: 'Error exporting image. Please try again.',
          errorCalculatingModel: 'Error calculating parabolic model:',
          errorUsingTurf: 'Error using Turf.js:',
          anglesNotEqual: 'Beta angles are not equal. Code was interrupted.',
          calculating: 'Calculating...',
          csvHeader: 'Layer,Date',
          thetaRadiusHeader: 'Theta,Radius',
          legendTitle: 'Legend - MepBay Model',
          legendDiffractionPoint: 'Diffraction point (1)',
          legendClosurePoint: 'Coastal control point (2)',
          legendTangentPoint: 'Predominant wave direction (3)',
          legendControlLine: 'Control line (Ro)',
          legendParabolicLine: 'Parabolic line: statistical beach shape (bay)',
        },
      },
      shoreS: {
        moduleName: 'Shore-S',
        acquisition: {
          title: 'SHORE-S',
          0: {
            title: 'Select the tool',
            description: 'Start the SHORE-S module or access the documentation.',
          },
          1: {
            description: 'Select the data source for image acquisition.',
          },
          2: {
            title: 'Modeled Data',
            header: 'SHORE-S: Modeled Data Upload',
            description: 'Define the coordinate reference system and upload the model results.',
          },
          periodAndImages: {
            title: 'Period and images',
            description:
              'Adjust the period and cloud filter and choose which images to use. You can readjust the period as many times as you like, without leaving this step.',
          },
          entry: {
            moduleCard: 'SHORE-S Module',
            moduleCardDesc: 'Validation of shoreline modeling results.',
            docsCard: 'Documentation',
            docsCardDesc: 'Access the ShorelineS sediment model (Roelvink et al, 2020) in MATLAB or adapted to Python.',
          },
          dataChooser: {
            crsLabel: 'Coordinate Reference System (CRS)',
            crsPlaceholder: 'e.g. EPSG:32722',
            modeledDataTitle: 'Model Results',
            modelFileLabel: 'Model output',
            modelFileHintMat:
              'MATfile: the ShorelineS sediment model output is directly compatible with the application.',
            modelFileHintCsv:
              'CSVs: a file containing the variables "time", "x" and "y", where each row is a shoreline point in time.',
            exampleCsv: 'CSV example — model output',
            restoredFileName: 'Previously uploaded file',
            parsingModel: 'Reading model file…',
            parseError: 'Could not parse the selected file.',
            temporalDetectedHint: 'Detected temporal interval:',
            temporalDetectedSeparator: 'to',
            dateLimitHint:
              "Allowed adjustment: {{min}} to {{max}} (up to {{months}} months beyond the model's period).",
            dateErrorTitle: 'Period outside the allowed range',
            dateErrorStartOutOfRange:
              "The start date ({{value}}) is outside the allowed range: {{min}} to {{max}} (up to {{months}} months beyond the model's period).",
            dateErrorEndOutOfRange:
              "The end date ({{value}}) is outside the allowed range: {{min}} to {{max}} (up to {{months}} months beyond the model's period).",
            dateErrorStartAfterEnd: 'The start date cannot be later than the end date.',
            dateErrorFieldHelper: 'Outside the allowed range ({{min}} to {{max}}).',
            dateStartLabel: 'Start date',
            dateEndLabel: 'End date',
            noTemporalData: 'This model has no temporal data — the workflow will go straight to reprojection.',
            mapLegendTitle: 'Legend:',
            mapLegendCoastlines: 'Modeled Coastlines',
            mapLegendRoi: 'Area of Interest (AOI)',
          },
          periodChooser: {
            userDatesLabel: 'Use dates provided by the user?',
            cloudFilterLabel: 'Maximum cloud coverage (%)',
            noImages: 'No images found for the selected period.',
            noRoi: 'Area of interest not defined. Go back and verify the model file was processed correctly.',
            loadingMore: 'Fetching images for the additional period…',
          },
          imageSelector: {
            listPlaceholder: 'No images available — go back and adjust the period or the cloud filter.',
          },
        },
        merge: {
          toleranceLabel: 'Temporal Tolerance (days)',
          directionLabel: 'Search Direction',
          directionForward: 'Forward',
          directionBackward: 'Backward',
          directionNearest: 'Nearest',
          help: {
            tooltip: 'About the parameters',
            title: 'Parameters',
            intro:
              'The parameters define how the modeled data is associated with the observations for the error computation.',
            tolerance:
              'Temporal tolerance: defines the maximum time interval, relative to the date of each observation, within which a modeled date may be selected for matching.',
            direction:
              'Search direction: defines how the modeled date is searched for, relative to the observation date:',
            forward: '• Forward: takes the closest modeled date after the observation, within the tolerance.',
            backward: '• Backward: takes the closest modeled date before the observation, within the tolerance.',
            nearest:
              '• Nearest: takes the closest modeled date within the tolerance in both directions, before or after the observation.',
          },
        },
        analysis: {
          title: 'Validation of Modeled Data',
          help: {
            title: 'Validation of Modeled Data',
            intro: 'To start the analysis, you must draw the baseline along the coast and configure the transects.',
            config:
              'The configuration includes setting the spacing between transects and the size (length) of each transect.',
            purpose:
              'This geometry is used to intersect the modeled and observed data, making it possible to compute the error between them.',
          },
          execute: 'Run analysis',
          cleaning: 'Matching modeled and observed data',
          metricsTitle: 'Error metrics',
          metrics: {
            modelSection: 'Simulation error (Modeled × Reference)',
            measuredSection: 'Observed variance over the period (Final × Initial)',
            unit: 'm',
            rmseAvg: {
              label: 'Mean RMSE of the Simulation',
            },
            biasAvg: {
              label: 'Mean Bias of the Simulation',
            },
            rmseLast6m: {
              label: 'RMSE (last 6 months)',
            },
            rmseFinal: {
              label: 'RMSE (final)',
            },
            biasFinal: {
              label: 'Bias (final)',
            },
            rmsm: {
              label: 'Measured RMS',
            },
            biasm: {
              label: 'Measured bias',
            },
            bss: {
              label: 'Brier Skill Score (BSS)',
              bad: 'Bad',
              poor: 'Poor',
              reasonable: 'Reasonable',
              good: 'Good',
              excellent: 'Excellent',
            },
          },
          togglePlots: 'Open plots',
          plotByDateTitle: 'Mean error over time',
          plotByTransectTitle: 'Mean error per transect',
          plotDateAxis: 'Date',
          plotTransectAxis: 'Transect ID',
          plotTransectShort: 'Transect',
          plotErrorAxis: 'Error (m)',
          plotRmsSeries: 'RMSE',
          plotBiasSeries: 'Bias',
          plotExportImage: 'Export as image',
        },
        exports: {
          title: 'Export results',
          pairs: 'Modeled x reference pairs (CSV)',
          metrics: 'Error metrics summary (CSV)',
          baselineKml: 'Baseline (KML)',
          baselineFolder: 'Baseline',
          downloadCsv: 'Download',
        },
        map: {
          noModelTitle: 'No model loaded',
          noModelMessage:
            'The modeled coastlines are not available — the page may have been reloaded or opened directly. Return to the beginning and load the model file again.',
          placeholder: 'Visualization screen for modeled and reference shorelines.',
          legendModeled: 'Modeled Coastlines',
          legendReference: 'Reference Coastlines',
          legendRaw: 'Extracted Coastlines',
        },
        extraction: {
          title: 'Coastline extraction',
          running: 'Extracting from the satellite imagery…',
          ready: '{{extracted}} of {{requested}} images processed.',
          retry: 'Try again',
          waitingTooltip: 'Wait for the coastline extraction to finish.',
        },
      },
    },
  },
};

export default en;
